import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLingua } from '../../context/LinguaContext';
import { V2_BASE } from '../config';
import {
  Home, ChevronRight, Pencil, Trash2, Clock, Shield, ShieldX, Lock, XCircle, X,
  CheckCircle2, Settings, AlertTriangle, Fingerprint, Star, Key, Printer, ShieldCheck,
  KeyRound, Check, Monitor, Smartphone, Eye, EyeOff, RotateCcw, Search,
  LayoutGrid, List, Boxes, Plus, Headphones, MoreVertical, Unlink, Filter,
  Users2, UserPlus, Download, SlidersHorizontal, Package, Info, ChevronDown,
  ChevronsUpDown, PlusCircle, Table2, Mail, Cloud,
  Send, UserCog, ClipboardList, UserX, ChevronLeft,
  Briefcase, Zap, Globe, GripVertical, Move, FolderPlus, Phone, Copy,
} from 'lucide-react';
import './contabilita.css';
import './account.css';

const api = axios.create({ withCredentials: true });

const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return '—'; } };
const fmtDateTime = (d) => { if (!d) return '—'; try { const t = new Date(d); return t.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' + t.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch { return '—'; } };
const initialsOf = (n, e) => { if (n) { const p = n.trim().split(/\s+/); return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase(); } return (e?.[0] || 'U').toUpperCase(); };

/* Opzioni campi profilo (header) */
const LINGUE = [{ v: 'it', l: 'Italiano' }, { v: 'en', l: 'English' }, { v: 'es', l: 'Español' }, { v: 'fr', l: 'Français' }, { v: 'de', l: 'Deutsch' }];
const PAESI = [{ v: 'IT', l: 'Italia', f: '🇮🇹' }, { v: 'CH', l: 'Svizzera', f: '🇨🇭' }, { v: 'FR', l: 'Francia', f: '🇫🇷' }, { v: 'DE', l: 'Germania', f: '🇩🇪' }, { v: 'ES', l: 'Spagna', f: '🇪🇸' }, { v: 'GB', l: 'Regno Unito', f: '🇬🇧' }];
const FUSI = ['Europe/Rome', 'Europe/Zurich', 'Europe/Paris', 'Europe/London', 'Europe/Madrid', 'Europe/Berlin'];

const useFetch = (url, key, fallback) => {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true; setLoading(true);
    api.get(url).then(r => { if (alive) setData(key ? (r.data?.[key] ?? fallback) : r.data); })
      .catch(() => { if (alive) setData(fallback); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [url, key]); // eslint-disable-line
  return [data, loading];
};

const Toggle = ({ on, onClick }) => (
  <button className={`nfx-toggle${on ? ' is-on' : ''}`} onClick={onClick} aria-pressed={on} type="button" />
);

/* ── Codice supporto: 6 cifre rigenerate ogni 180s (deterministico lato server) ── */
const SupportCode = ({ variant = 'card' }) => {
  const [info, setInfo] = useState(null);   // { code, ttl, period }
  const [ttl, setTtl] = useState(0);
  const load = React.useCallback(() => {
    api.get('/api/codice-supporto')
      .then(r => { setInfo(r.data); setTtl(r.data.ttl); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => {
      setTtl(prev => {
        if (prev <= 1) { load(); return info?.period || 180; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [load, info?.period]);

  const period = info?.period || 180;
  const pct = Math.max(0, Math.min(100, (ttl / period) * 100));
  const code = info?.code ? info.code.replace(/(\d{3})(\d{3})/, '$1 $2') : '— — —';

  if (variant === 'page') {
    return (
      <div className="nfx-supcode">
        <div className="nfx-acc-code nfx-supcode__val">{code}</div>
        <div className="nfx-supcode__bar"><span style={{ width: `${pct}%` }} /></div>
        <div className="nfx-supcode__meta">Si aggiorna tra {ttl}s</div>
      </div>
    );
  }
  return (
    <>
      <div className="nfx-acc-sup__code">{code} <Info size={14} className="nfx-acc-i" /></div>
      <div className="nfx-acc-sup__bar"><span style={{ width: `${pct}%` }} /></div>
    </>
  );
};

/* ── Modal generico ── */
const V2Modal = ({ title, onClose, children, footer }) => {
  const { t } = useLingua();
  return (
  <div className="nfx-modal-scrim" onClick={() => onClose()}>
    <div className="nfx-modal" onClick={e => e.stopPropagation()}>
      <div className="nfx-modal__head">
        <span className="nfx-modal__title">{typeof title === 'string' ? t(title) : title}</span>
        <button className="nfx-modal__close" onClick={() => onClose()} aria-label="Chiudi"><X size={20} /></button>
      </div>
      <div className="nfx-modal__body">{children}</div>
      {footer && <div className="nfx-modal__foot">{footer}</div>}
    </div>
  </div>
  );
};

/* ── Modal: modifica e-mail di accesso ── */
const EmailConnModal = ({ email, onClose }) => {
  const [nw, setNw] = useState('');
  const [cf, setCf] = useState('');
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [err, setErr] = useState('');
  const valid = /.+@.+\..+/.test(nw) && nw === cf && pw.length > 0;
  const submit = () => {
    if (!valid) return;
    api.put('/api/profilo', { email: nw, password_conferma: pw })
      .then(() => onClose(nw))
      .catch(() => setErr("Impossibile aggiornare l'e-mail. Verifica la password inserita."));
  };
  return (
    <V2Modal title="Modifica il tuo indirizzo e-mail di accesso" onClose={() => onClose()}
      footer={<>
        <button className="nfx-btn-sec" onClick={() => onClose()}>Annullare</button>
        <button className={`nfx-btn-pri${valid ? '' : ' is-disabled'}`} disabled={!valid} onClick={submit}>Avanti</button>
      </>}>
      <p className="nfx-modal__p">E-mail di accesso attuale: <strong>{email}</strong></p>
      <div className="nfx-fld">
        <label className="nfx-fld__label">Nuova e-mail di accesso *</label>
        <input className="nfx-fld__input" type="email" value={nw} onChange={e => { setNw(e.target.value); setErr(''); }} autoFocus />
      </div>
      <div className="nfx-fld">
        <label className="nfx-fld__label">Conferma il nuovo indirizzo e-mail di accesso *</label>
        <input className="nfx-fld__input" type="email" value={cf} onChange={e => setCf(e.target.value)} />
      </div>
      <p className="nfx-modal__p">Per convalidare l'aggiornamento inserisci <strong>la password di accesso attuale.</strong></p>
      <div className="nfx-fld">
        <label className="nfx-fld__label">Password di accesso *</label>
        <div className="nfx-fld__pw">
          <input className="nfx-fld__input" type={show ? 'text' : 'password'} value={pw} onChange={e => { setPw(e.target.value); setErr(''); }} />
          <button type="button" className="nfx-fld__eye" onClick={() => setShow(s => !s)}>{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
        </div>
      </div>
      {err && <div className="nfx-fld__err">{err}</div>}
    </V2Modal>
  );
};

/* ── Colore avatar deterministico per utente (tinte piatte) ── */
const AVATAR_COLORS = ['#e6007e', '#00a1b9', '#7b61ff', '#1d3c51', '#ff8500', '#16a36a', '#f2357a', '#9c26b0', '#00897b', '#5c6bc0'];
const avatarColor = (str) => {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

/* ── Modal: modifica informazioni personali (Nome / Cognome) ── */
const EditPersonalModal = ({ nome, cognome, onClose }) => {
  const [n, setN] = useState(nome || '');
  const [c, setC] = useState(cognome || '');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const valid = n.trim() && c.trim();
  const submit = () => {
    if (!valid || saving) return;
    setSaving(true);
    api.put('/api/profilo', { nome: n.trim(), cognome: c.trim() })
      .then(() => onClose({ nome: n.trim(), cognome: c.trim() }))
      .catch(() => { setErr('Impossibile salvare. Riprova.'); setSaving(false); });
  };
  return (
    <V2Modal title="Modifica le informazioni personali" onClose={() => onClose()}
      footer={<>
        <button className="nfx-btn-sec" onClick={() => onClose()}>Annullare</button>
        <button className={`nfx-btn-pri${valid ? '' : ' is-disabled'}`} disabled={!valid || saving} onClick={submit}>Registrare</button>
      </>}>
      <div className="nfx-fld">
        <label className="nfx-fld__label">Nome *</label>
        <input className="nfx-fld__input" value={n} onChange={e => { setN(e.target.value); setErr(''); }} autoFocus />
      </div>
      <div className="nfx-fld">
        <label className="nfx-fld__label">Cognome *</label>
        <input className="nfx-fld__input" value={c} onChange={e => { setC(e.target.value); setErr(''); }} />
      </div>
      {err && <div className="nfx-fld__err">{err}</div>}
    </V2Modal>
  );
};

/* ── Modal: eliminazione account (contenuto dinamico su servizi attivi) ── */
const DeleteAccountModal = ({ name, onClose }) => {
  const [servizi] = useFetch('/api/servizi/riepilogo', 'servizi', []);
  const list = Array.isArray(servizi) ? servizi : [];
  const hasServices = list.length > 0;
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const conferma = () => {
    if (sending) return;
    setSending(true);
    api.post('/api/richieste-cancellazione', {})
      .then(() => setSent(true))
      .catch(() => setSent(true))
      .finally(() => setSending(false));
  };
  if (sent) {
    return (
      <V2Modal title="" onClose={onClose} footer={<button className="nfx-btn-pri" onClick={onClose} style={{ marginLeft: 'auto' }}>Chiudi</button>}>
        <div className="nfx-del__illu"><span className="nfx-del__illu-ic" style={{ background: '#16a36a' }}><Info size={30} /></span></div>
        <div className="nfx-del__title">Richiesta presa in carico. Il nostro team ti contatterà per completare la procedura.</div>
      </V2Modal>
    );
  }
  return (
    <V2Modal title="" onClose={onClose}
      footer={<>
        <button className="nfx-btn-sec" onClick={onClose}>Annullare</button>
        <button className="nfx-btn-pri" disabled={sending} onClick={conferma}>Dichiaro di aver letto e capito</button>
      </>}>
      <div className="nfx-del__illu"><span className="nfx-del__illu-ic"><AlertTriangle size={30} /></span></div>
      <div className="nfx-del__title">Stai per <strong>eliminare definitivamente</strong> il tuo account. Continuare veramente?</div>
      <div className="nfx-del__alert">
        <Info size={18} className="nfx-del__alert-ic" />
        <span>Il tuo utente <strong>{name}</strong> è il responsabile legale o l&rsquo;ultimo contatto di fatturazione sull&rsquo;account.</span>
      </div>
      <ul className="nfx-del__list">
        <li>Per rimuovere il tuo utente <strong>{name}</strong>, devi prima designare un altro responsabile legale o un contatto di fatturazione dell&rsquo;organizzazione.</li>
        {hasServices ? (
          <>
            <li>Hai <strong>{list.length}</strong> {list.length === 1 ? 'servizio attivo' : 'servizi attivi'}: prima di eliminare l&rsquo;account devi disdirli o trasferirli.</li>
            {list.map((s) => (
              <li key={s.id} className="nfx-del__svc">
                <XCircle size={16} className="nfx-del__svc-ic" />
                <span>{s.nome}{s.tipo_prodotto ? <span className="nfx-del__svc-type"> · {s.tipo_prodotto}</span> : null}</span>
              </li>
            ))}
          </>
        ) : (
          <li>Se sei l&rsquo;ultimo utente della tua organizzazione, puoi eliminarla dalla pagina &laquo;Organizzazione&raquo;. Per completare l&rsquo;eliminazione dell&rsquo;account, contatta l&rsquo;assistenza.</li>
        )}
      </ul>
    </V2Modal>
  );
};

/* ── Modal: le mie organizzazioni ── */
const OrganizationsModal = ({ orgName, initials, onClose }) => (
  <V2Modal title="Le mie organizzazioni" onClose={() => onClose()}
    footer={<button className="nfx-btn-sec" onClick={() => onClose()}>Chiudi</button>}>
    <p className="nfx-modal__p">Il tuo profilo utente è collegato a <strong>1 organizzazione.</strong></p>
    <div className="nfx-org__create">
      <a className="nfx-org__createbtn" href="https://welcome.neoflux.it" target="_blank" rel="noreferrer">
        <Plus size={18} /> Creare una nuova organizzazione
      </a>
    </div>
    <div className="nfx-org__item">
      <div className="nfx-org__info">
        <span className="nfx-org__logo" style={{ background: avatarColor(orgName) }}>{initials}</span>
        <div className="nfx-org__txt">
          <div className="nfx-org__name">{orgName}</div>
          <div className="nfx-org__role">Responsabile legale</div>
        </div>
      </div>
      <div className="nfx-org__actions">
        <span className="nfx-org__current" title="Organizzazione corrente"><CheckCircle2 size={18} /></span>
        <button className="nfx-org__unlink" disabled title="Non puoi scollegarti dalla tua organizzazione attuale"><Unlink size={16} /></button>
      </div>
    </div>
  </V2Modal>
);

/* ── Modal: newsletter ── */
const NewsletterModal = ({ onClose }) => {
  const [prefs, setPrefs] = useState(null);
  const [on, setOn] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/api/preferenze-comunicazione')
      .then(r => { setPrefs(r.data || {}); setOn(!!r.data?.email_newsletter); })
      .catch(() => setPrefs({}));
  }, []);
  const save = () => {
    if (saving) return;
    setSaving(true);
    api.put('/api/preferenze-comunicazione', { ...(prefs || {}), email_newsletter: on })
      .then(() => onClose(on))
      .catch(() => setSaving(false));
  };
  return (
    <V2Modal title="Newsletter" onClose={() => onClose()}
      footer={<>
        <button className="nfx-btn-sec" onClick={() => onClose()}>Chiudi</button>
        <button className="nfx-btn-pri" disabled={saving} onClick={save} style={{ marginLeft: 'auto' }}>Registrare</button>
      </>}>
      <p className="nfx-modal__p" style={{ marginBottom: 30 }}>Seleziona <strong>le tematiche NeoFlux</strong> per ricevere le newsletter relative ad esse.</p>
      <label className="nfx-news__row">
        <Toggle on={on} onClick={() => setOn(v => !v)} />
        <span className="nfx-news__label">Novità NeoFlux</span>
      </label>
    </V2Modal>
  );
};

/* ── Modal: impostazioni di sicurezza (notifiche e-mail) ── */
const SecuritySettingsModal = ({ v, onSave, onClose }) => {
  const [f, setF] = useState({
    blocco: !!v.blocco_account_tentativi,
    falliti: !!v.notifica_tentativi_falliti,
    riuscite: !!v.notifica_connessioni_riuscite,
    tentativi: v.numero_tentativi || 20,
  });
  const t = (k) => setF(s => ({ ...s, [k]: !s[k] }));
  const submit = () => {
    onSave({ blocco_account_tentativi: f.blocco, notifica_tentativi_falliti: f.falliti, notifica_connessioni_riuscite: f.riuscite, numero_tentativi: Number(f.tentativi) || 20 });
    onClose();
  };
  return (
    <V2Modal title="Impostazioni di sicurezza" onClose={() => onClose()}
      footer={<>
        <button className="nfx-btn-sec" onClick={() => onClose()}>Chiudi</button>
        <button className="nfx-btn-pri" onClick={submit}>Registrare</button>
      </>}>
      <p className="nfx-modal__p">Seleziona i casi per i quali vuoi essere avvisato tramite e-mail. <Info size={14} className="nfx-acc-i" style={{ verticalAlign: '-2px' }} /></p>
      <ul className="nfx-tgl-list">
        <li className="nfx-tgl-row"><Toggle on={f.blocco} onClick={() => t('blocco')} /><span>Blocco dell'account dopo il numero predefinito di tentativi di connessione consecutivi non riusciti.</span></li>
        {f.blocco && (
          <li className="nfx-tgl-sub">
            <span className="nfx-tgl-sub__label">Numero di tentativi :</span>
            <input className="nfx-tgl-sub__input" type="number" min="1" value={f.tentativi} onChange={e => setF(s => ({ ...s, tentativi: e.target.value }))} />
          </li>
        )}
        <li className="nfx-tgl-row"><Toggle on={f.falliti} onClick={() => t('falliti')} /><span>Tentativi di connessione non riusciti</span></li>
        <li className="nfx-tgl-row"><Toggle on={f.riuscite} onClick={() => t('riuscite')} /><span>Connessioni riuscite</span></li>
      </ul>
    </V2Modal>
  );
};

/* ── Modal: e-mail di recupero ── */
const RecoveryEmailModal = ({ current, onSave, onClose }) => {
  const [em, setEm] = useState('');
  const [cf, setCf] = useState('');
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const valid = /.+@.+\..+/.test(em) && em === cf && pw.length > 0;
  const submit = () => { if (!valid) return; onSave(em); onClose(); };
  return (
    <V2Modal title={current ? "Modifica l'indirizzo e-mail di recupero" : 'Aggiungi un indirizzo e-mail di recupero'} onClose={() => onClose()}
      footer={<>
        <button className="nfx-btn-sec" onClick={() => onClose()}>Annullare</button>
        <button className={`nfx-btn-pri${valid ? '' : ' is-disabled'}`} disabled={!valid} onClick={submit}>Registrare</button>
      </>}>
      <p className="nfx-modal__p">Consigliamo di aggiungere un <strong>indirizzo e-mail di recupero</strong> al tuo account. Questo potrà essere utilizzato per reimpostare la password qualora tu la dimentichi.</p>
      <div className="nfx-fld">
        <label className="nfx-fld__label">E-mail di recupero *</label>
        <input className="nfx-fld__input" type="email" value={em} onChange={e => setEm(e.target.value)} autoFocus />
      </div>
      <div className="nfx-fld">
        <label className="nfx-fld__label">Conferma l'indirizzo e-mail *</label>
        <input className="nfx-fld__input" type="email" value={cf} onChange={e => setCf(e.target.value)} />
      </div>
      <p className="nfx-modal__p">Per convalidare l'aggiornamento inserisci <strong>la password di accesso attuale.</strong></p>
      <div className="nfx-fld">
        <label className="nfx-fld__label">Password *</label>
        <div className="nfx-fld__pw">
          <input className="nfx-fld__input" type={show ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} />
          <button type="button" className="nfx-fld__eye" onClick={() => setShow(s => !s)}>{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
        </div>
      </div>
    </V2Modal>
  );
};

/* ── Modal: telefono di recupero ── */
const PREFIXES = ['+39', '+41', '+33', '+49', '+44', '+34', '+1'];
const PhoneRecoveryModal = ({ initial, onSave, onClose }) => {
  const m = /^(\+\d{1,3})?\s*(.*)$/.exec(initial || '') || [];
  const [prefix, setPrefix] = useState(PREFIXES.includes(m[1]) ? m[1] : '+39');
  const [num, setNum] = useState(m[2] || '');
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const valid = num.replace(/\D/g, '').length >= 6 && pw.length > 0;
  const submit = () => { if (!valid) return; onSave(`${prefix}${num.replace(/\s+/g, '')}`); onClose(); };
  return (
    <V2Modal title="Modifica il tuo numero di recupero" onClose={() => onClose()}
      footer={<>
        <button className="nfx-btn-sec" onClick={() => onClose()}>Chiudi</button>
        <button className={`nfx-btn-pri${valid ? '' : ' is-disabled'}`} disabled={!valid} onClick={submit}>Avanti</button>
      </>}>
      <p className="nfx-modal__p">Quale numero di telefono vuoi utilizzare come numero di recupero? Questo numero sarà utilizzato in caso di perdita della tua password.</p>
      <div className="nfx-fld">
        <label className="nfx-fld__label">Telefono *</label>
        <div className="nfx-phone">
          <select className="nfx-phone__pfx" value={prefix} onChange={e => setPrefix(e.target.value)}>
            {PREFIXES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input className="nfx-fld__input" type="tel" placeholder="78 123 45 67" value={num} onChange={e => setNum(e.target.value)} autoFocus />
        </div>
      </div>
      <div className="nfx-fld">
        <label className="nfx-fld__label">Password *</label>
        <div className="nfx-fld__pw">
          <input className="nfx-fld__input" type={show ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} />
          <button type="button" className="nfx-fld__eye" onClick={() => setShow(s => !s)}>{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
        </div>
      </div>
    </V2Modal>
  );
};

/* Breadcrumb: home > Il mio profilo > [current] */
const AccCrumb = ({ current, parents = [] }) => {
  const navigate = useNavigate();
  const { t } = useLingua();
  return (
    <div className="nfx-crumb">
      <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
      <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
      {current
        ? <><button className="nfx-crumb__link" onClick={() => navigate(V2_BASE + '/account')}>{t('Il mio profilo')}</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            {parents.map(p => (
              <React.Fragment key={p.path}>
                <button className="nfx-crumb__link" onClick={() => navigate(V2_BASE + p.path)}>{t(p.label)}</button>
                <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
              </React.Fragment>
            ))}
            <span className="nfx-crumb__cur">{t(current)}</span></>
        : <span className="nfx-crumb__cur">{t('Il mio profilo')}</span>}
    </div>
  );
};

/* Breadcrumb: home > [label] */
const Crumb1 = ({ label }) => {
  const navigate = useNavigate();
  return (
    <div className="nfx-crumb">
      <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
      <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
      <span className="nfx-crumb__cur">{label}</span>
    </div>
  );
};

/* Suite collaborativa: tile app (de-brandizzate, icone neutre) */
const SUITE_APPS = [
  { Icon: Mail, bg: '#7A5AF8', title: 'Posta' },
  { Icon: Cloud, bg: '#2F6FED', title: 'Drive' },
];
const roleLabel = (r) => {
  if (r === 'customer_owner') return 'Responsabile legale';
  if (r === 'customer_admin') return 'Amministratore';
  if (r === 'customer_accountant') return 'Contabile';
  return 'Utente';
};
const ROLE_OPTIONS = [
  { value: 'customer_user', label: 'Utente' },
  { value: 'customer_admin', label: 'Amministratore' },
  { value: 'customer_accountant', label: 'Contabile' },
];

/* ═══════════════════ 0. UTENTI ═══════════════════ */
export const UtentiPage = () => {
  const navigate = useNavigate();
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [menuId, setMenuId] = useState(null);
  const [orgFilterOpen, setOrgFilterOpen] = useState(false);
  const [userModal, setUserModal] = useState(null); // { type: 'role'|'groups'|'reset'|'delete', user }

  const load = () => {
    setLoading(true);
    api.get('/api/utenti')
      .then(r => setUtenti(Array.isArray(r.data?.utenti) ? r.data.utenti : []))
      .catch(() => setUtenti([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = utenti.filter(u => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (u.nome_completo || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s);
  });

  return (
    <div className="nfx-cb">
      <Crumb1 label="Gestione degli utenti" />
      <div className="nfx-card">
        <div className="nfx-tb">
          <span className="nfx-tb__count"><b>{filtered.length}</b> Utenti</span>
          <button className="nfx-tb__btn" onClick={() => navigate(V2_BASE + '/account/utenti/aggiungi')}>
            <UserPlus size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />Aggiungere un utente
          </button>
          <span className="nfx-ul-selwrap">
            <button className="nfx-ul-selbtn" onClick={() => setOrgFilterOpen(v => !v)}>
              <span className="nfx-ul-selbtn-ico"><Users2 size={16} /></span>
              Utenti dell'organizzazione
              <ChevronDown size={12} className="nfx-ul-selbtn-chev" />
            </button>
            {orgFilterOpen && (
              <div className="nfx-ul-fp" onMouseLeave={() => setOrgFilterOpen(false)}>
                <div className="nfx-ul-fp-sec nfx-ul-fp-sec--first">
                  <button className="nfx-ul-fp-item" onClick={() => setOrgFilterOpen(false)}><span className="nfx-ul-fp-ico"><Users2 size={20} /></span>Tutti gli utenti dell'organizzazione</button>
                  <button className="nfx-ul-fp-item" onClick={() => setOrgFilterOpen(false)}><span className="nfx-ul-fp-ico"><UserX size={20} /></span>Utenti senza accesso</button>
                </div>
              </div>
            )}
          </span>
          <span className="nfx-tb__spacer" />
          <div className="nfx-tb__actions">
            <button className="nfx-iconbtn"><List size={18} /></button>
            <button className={`nfx-iconbtn${showSearch ? ' is-active' : ''}`} onClick={() => setShowSearch(v => !v)} title="Cerca e filtra"><Search size={18} /></button>
          </div>
        </div>

        {showSearch && (
          <div className="nfx-utb-filters">
            <div className="nfx-utb-search">
              <Search size={16} />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Inserisci cognome, nome o e-mail" />
              {q && <button className="nfx-utb-clear" onClick={() => setQ('')} aria-label="Cancella"><X size={14} /></button>}
            </div>
            <button className="nfx-utb-addfilter"><Plus size={15} /> Aggiungere un filtro</button>
          </div>
        )}

        <table className="nfx-table">
          <thead>
            <tr>
              <th><span className="nfx-th-s"><ChevronsUpDown size={13} /> Utente</span></th>
              <th><span className="nfx-th-s"><ChevronsUpDown size={13} /> Ruolo</span></th>
              <th>Gruppi</th>
              <th><span className="nfx-th-s">Suite collaborativa <Info size={13} /></span></th>
              <th><span className="nfx-th-s">Prodotti <Info size={13} /></span></th>
              <th className="nfx-td-actions"><button className="nfx-iconbtn" title="Colonne"><Table2 size={16} /></button></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const pending = u.stato === 'in_attesa';
              return (
                <tr key={u.id}>
                  <td>
                    <span className="nfx-ul-user">
                      {pending
                        ? <span className="nfx-ul-avatar nfx-ul-avatar--pending"><Send size={20} /></span>
                        : <span className="nfx-ul-avatar">{initialsOf(u.nome_completo, u.email)}</span>}
                      <span className="nfx-ul-idcol">
                        <span className="nfx-ul-name">{u.nome_completo}{pending && <span className="nfx-ul-pending">In attesa</span>}</span>
                        <span className="nfx-ul-email">{u.email}</span>
                      </span>
                    </span>
                  </td>
                  <td>{roleLabel(u.ruolo)}</td>
                  <td><button className="nfx-ul-addcircle" title="Aggiungi a un gruppo"><PlusCircle size={16} /></button></td>
                  <td>
                    {u.is_owner ? (
                      <span className="nfx-ul-access">
                        {SUITE_APPS.map((a, i) => (
                          <span key={i} className="nfx-ul-access-item" title={a.title}><span className="nfx-ul-access-tile" style={{ background: a.bg }} /></span>
                        ))}
                        <span className="nfx-ul-access-item nfx-ul-access-item--more" title="Altre applicazioni">+1</span>
                      </span>
                    ) : <span className="nfx-ul-dash" />}
                  </td>
                  <td>{u.is_owner ? <span className="nfx-ul-products">Tutti <Info size={14} /></span> : <span className="nfx-ul-dash" />}</td>
                  <td className="nfx-td-actions">
                    <span className="nfx-ul-rowactions">
                      <button className={`nfx-ul-more${menuId === u.id ? ' is-open' : ''}`} onClick={() => setMenuId(menuId === u.id ? null : u.id)} title="Azioni"><MoreVertical size={20} /></button>
                      {menuId === u.id && (
                        <div className="nfx-ul-ctx" onMouseLeave={() => setMenuId(null)}>
                          {pending ? (
                            <>
                              <button className="nfx-ul-ctx-item" onClick={() => { setMenuId(null); navigate(V2_BASE + `/account/utenti/${u.id}`); }}><UserCog size={16} />Gestisci l'utente</button>
                              <button className="nfx-ul-ctx-item nfx-ul-ctx-item--danger" onClick={() => { setMenuId(null); setUserModal({ type: 'delete', user: u }); }}><XCircle size={16} />Eliminare l'invito</button>
                            </>
                          ) : (
                            <>
                              <button className="nfx-ul-ctx-item" onClick={() => { setMenuId(null); navigate(V2_BASE + `/account/utenti/${u.id}`); }}><UserCog size={16} />Gestisci l'utente</button>
                              <button className="nfx-ul-ctx-item" onClick={() => { setMenuId(null); setUserModal({ type: 'role', user: u }); }}><Pencil size={16} />Modifica il ruolo</button>
                              <button className="nfx-ul-ctx-item" onClick={() => { setMenuId(null); setUserModal({ type: 'groups', user: u }); }}><Users2 size={16} />Modifica i gruppi</button>
                              <button className="nfx-ul-ctx-item nfx-ul-ctx-item--divider" onClick={() => { setMenuId(null); navigate(V2_BASE + '/account/rapporto-attivita'); }}><ClipboardList size={16} />Controlla l'attività</button>
                              <button className="nfx-ul-ctx-item" onClick={() => { setMenuId(null); setUserModal({ type: 'reset', user: u }); }}><KeyRound size={16} />Reimposta la password</button>
                              {u.is_owner
                                ? <button className="nfx-ul-ctx-item nfx-ul-ctx-item--disabled" disabled><XCircle size={16} />Rimuovi dall'organizzazione</button>
                                : <button className="nfx-ul-ctx-item nfx-ul-ctx-item--danger" onClick={() => { setMenuId(null); setUserModal({ type: 'delete', user: u }); }}><XCircle size={16} />Rimuovi dall'organizzazione</button>}
                            </>
                          )}
                        </div>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6}><div className="nfx-empty2" style={{ padding: '32px 0' }}><span className="nfx-empty2__icon"><Users2 size={36} /></span><div className="nfx-empty2__title">Nessun utente</div></div></td></tr>
            )}
          </tbody>
        </table>
        <div className="nfx-pager"><div className="nfx-pager__left"><span>Risultati per pagina</span><select defaultValue="25"><option>25</option><option>50</option><option>100</option></select></div><span>Pagina 1 di 1</span></div>
      </div>

      {userModal?.type === 'role' && <RoleModal user={userModal.user} onClose={() => setUserModal(null)} onSaved={() => { setUserModal(null); load(); }} />}
      {userModal?.type === 'groups' && <GroupsModal user={userModal.user} onClose={() => setUserModal(null)} onSaved={() => setUserModal(null)} />}
      {userModal?.type === 'reset' && <ResetPwdModal user={userModal.user} onClose={() => setUserModal(null)} />}
      {userModal?.type === 'delete' && <DeleteUserModal user={userModal.user} onClose={() => setUserModal(null)} onDeleted={() => { setUserModal(null); load(); }} />}
    </div>
  );
};

/* ═══════════════════ 0a. WIZARD — Aggiungere un utente ═══════════════════ */
const ROLE_INFO = [
  { value: 'customer_owner', label: 'Responsabile legale', desc: ["Responsabilità legale dell'organizzazione", 'Può gestire tutti i prodotti e tutti gli utenti', 'Può gestire la contabilità e gli ordini di nuovi prodotti', 'Può gestire tutti i clienti'] },
  { value: 'customer_admin', label: 'Amministratore', desc: ['Può gestire tutti i prodotti e tutti gli utenti', 'Può gestire la contabilità e gli ordini di nuovi prodotti', 'Può gestire tutti i clienti'] },
  { value: 'customer_user', label: 'Utente', desc: ['Può gestire i prodotti che autorizzerai', 'Può gestire la contabilità, se lo autorizzi', 'Può gestire i clienti che autorizzi'] },
];

/* Campo con etichetta flottante (module-level: NON ricrearlo nel render → niente perdita di focus) */
const WzField = ({ label, value, onChange, type = 'text' }) => (
  <label className={`nfx-wz-field${value ? ' is-filled' : ''}`}>
    <span className="nfx-wz-field__lbl">{label}</span>
    <input type={type} value={value} onChange={onChange} placeholder={label} />
  </label>
);

export const UtentiInvitaPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [email, setEmail] = useState('');
  const [ruolo, setRuolo] = useState('customer_user');
  const [roleOpen, setRoleOpen] = useState(false);
  const [billing, setBilling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [exitOpen, setExitOpen] = useState(false);

  const dirty = !!(nome || cognome || email);
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const step1Valid = nome.trim() && cognome.trim() && emailValid;
  const roleObj = ROLE_INFO.find(r => r.value === ruolo) || ROLE_INFO[2];
  const privileged = ruolo === 'customer_owner' || ruolo === 'customer_admin';

  const goList = () => navigate(V2_BASE + '/account/utenti');
  const cancel = () => { if (dirty) setExitOpen(true); else goList(); };
  const submit = () => {
    setSaving(true); setError('');
    api.post('/api/utenti', { email: email.trim(), nome: nome.trim(), cognome: cognome.trim(), ruolo })
      .then(goList)
      .catch(e => { setError(e.response?.data?.messaggio || 'Errore durante l\'invito.'); setSaving(false); });
  };

  return (
    <div className="nfx-cb">
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <button className="nfx-crumb__link" onClick={goList}>Gestione degli utenti</button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <span className="nfx-crumb__cur">Aggiungere un utente</span>
      </div>

      <div className="nfx-wz-steps">
        <div className={`nfx-wz-step${step === 1 ? ' is-current' : ' is-done'}`}><span className="nfx-wz-step__lbl">Informazioni</span><span className="nfx-wz-step__bar" /></div>
        <div className={`nfx-wz-step${step === 2 ? ' is-current' : ''}`}><span className="nfx-wz-step__lbl">{step === 2 ? 'Conferma' : ''}</span><span className="nfx-wz-step__bar" /></div>
      </div>

      <div className="nfx-wz-card">
        {step === 1 && (
          <>
            <div className="nfx-wz-block">
              <h2 className="nfx-wz-title">Informazioni generali</h2>
              <div className="nfx-wz-fields">
                <WzField label="Nome *" value={nome} onChange={e => setNome(e.target.value)} />
                <WzField label="Cognome *" value={cognome} onChange={e => setCognome(e.target.value)} />
              </div>
            </div>

            <div className="nfx-wz-block">
              <h3 className="nfx-wz-subtitle">Ruolo nell'organizzazione</h3>
              <div className="nfx-wz-selwrap">
                <button className={`nfx-wz-sel${roleOpen ? ' is-open' : ''}`} onClick={() => setRoleOpen(v => !v)}>
                  <span>{roleObj.label}</span><ChevronDown size={14} />
                </button>
                {roleOpen && (
                  <div className="nfx-wz-selpanel" onMouseLeave={() => setRoleOpen(false)}>
                    {ROLE_INFO.map(r => (
                      <div key={r.value} className={`nfx-wz-roleopt${r.value === ruolo ? ' is-selected' : ''}`} onClick={() => { setRuolo(r.value); setRoleOpen(false); }}>
                        <div className="nfx-wz-roleopt__t">{r.label}</div>
                        <ul>{r.desc.map((d, i) => <li key={i}>{d}</li>)}</ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {privileged && (
                <div className="nfx-wz-alert nfx-wz-alert--warn">
                  <AlertTriangle size={16} />
                  <span>Questo utente potrà <b>gestire tutti i prodotti e gli utenti dell'organizzazione</b>{ruolo === 'customer_owner' ? ', compreso te.' : ', ad eccezione dei tutori legali.'}</span>
                </div>
              )}
              <div className="nfx-wz-switchrow">
                <span>Autorizza l'utente a <b>gestire la contabilità</b> per NeoFlux e a <b>ricevere e-mail ed SMS di rinnovo</b></span>
                <Toggle on={privileged || billing} onClick={() => !privileged && setBilling(v => !v)} />
              </div>
            </div>

            <div className="nfx-wz-block">
              <h3 className="nfx-wz-subtitle">Come accederà l'utente?</h3>
              <span className="nfx-wz-desc">L'invito viene inviato all'indirizzo e-mail personale dell'utente. Comparirà come «In attesa» finché non completa la registrazione con una password propria.</span>
              <div style={{ maxWidth: 378 }}>
                <WzField label="Indirizzo e-mail *" value={email} onChange={e => setEmail(e.target.value)} type="email" />
              </div>
            </div>

            <div className="nfx-wz-actions">
              <button className="nfx-btn-grey" onClick={cancel}>Annullare</button>
              <button className="nfx-btn-blue" disabled={!step1Valid} onClick={() => setStep(2)}>Avanti</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="nfx-wz-block">
              <h2 className="nfx-wz-title" style={{ marginBottom: 24 }}>Conferma</h2>
              <span className="nfx-wz-desc">Controlla i dati dell'invito prima di inviarlo.</span>
              <div className="nfx-wz-recap">
                <span className="nfx-ul-avatar nfx-ul-avatar--pending"><Send size={20} /></span>
                <div className="nfx-ul-idcol">
                  <span className="nfx-ul-name">{[nome, cognome].filter(Boolean).join(' ')}</span>
                  <span className="nfx-ul-email">{email}</span>
                </div>
                <span className="nfx-wz-recap__role">{roleObj.label}</span>
              </div>
              {error && <div className="nfx-wz-alert nfx-wz-alert--warn"><AlertTriangle size={16} /><span>{error}</span></div>}
            </div>
            <div className="nfx-wz-actions">
              <button className="nfx-btn-grey" onClick={cancel}>Annullare</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="nfx-btn-grey nfx-btn-grey--icon" onClick={() => setStep(1)} aria-label="Indietro"><ChevronLeft size={16} /></button>
                <button className="nfx-btn-blue" onClick={submit} disabled={saving}>{saving ? 'Invio…' : 'Invita'}</button>
              </div>
            </div>
          </>
        )}
      </div>

      {exitOpen && (
        <div className="nfx-dlg-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) setExitOpen(false); }}>
          <div className="nfx-dlg" style={{ maxWidth: 560 }}>
            <div className="nfx-dlg__head"><h3>Esci dalla pagina</h3><button className="nfx-dlg__x" onClick={() => setExitOpen(false)}><X size={18} /></button></div>
            <div className="nfx-dlg__body">
              <div className="nfx-wz-alert nfx-wz-alert--warn"><AlertTriangle size={16} /><span>Le tue modifiche non verranno salvate.</span></div>
              <span>Uscire veramente da questa pagina?</span>
            </div>
            <div className="nfx-dlg__foot">
              <button className="nfx-btn-grey" onClick={() => setExitOpen(false)}>Annullare</button>
              <button className="nfx-btn-blue" onClick={goList}>Esci</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════ 0d. MODALI AZIONI UTENTE (riusabili) ═══════════════════ */
const RoleModal = ({ user, onClose, onSaved }) => {
  const [ruolo, setRuolo] = useState(user.ruolo || 'customer_user');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const save = () => {
    setSaving(true); setErr('');
    api.patch(`/api/utenti/${user.id}`, { ruolo }).then(() => { if (onSaved) onSaved(ruolo); onClose(); })
      .catch(e => { setErr(e.response?.data?.messaggio || 'Errore durante il salvataggio.'); setSaving(false); });
  };
  return (
    <div className="nfx-dlg-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nfx-dlg" style={{ maxWidth: 600 }}>
        <div className="nfx-dlg__head"><h3>Modifica il ruolo di {user.nome_completo}</h3><button className="nfx-dlg__x" onClick={onClose}><X size={18} /></button></div>
        <div className="nfx-dlg__body">
          <div className="nfx-um-row"><span className="nfx-um-lbl">Ruolo nell'organizzazione</span>
            <select className="nfx-um-select" value={ruolo} onChange={e => setRuolo(e.target.value)}>
              {ROLE_INFO.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {err && <div className="nfx-dlg__nota" style={{ color: '#d71e04', background: '#ffe9e6', borderColor: '#f5c6c0' }}>{err}</div>}
        </div>
        <div className="nfx-dlg__foot"><button className="nfx-btn-grey" onClick={onClose}>Annullare</button><button className="nfx-btn-blue" disabled={saving} onClick={save}>{saving ? 'Salvataggio…' : 'Registrare'}</button></div>
      </div>
    </div>
  );
};

const GroupsModal = ({ user, onClose, onSaved }) => {
  const [all, setAll] = useState([]);
  const [sel, setSel] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([api.get('/api/gruppi'), api.get(`/api/utenti/${user.id}/gruppi`)])
      .then(([a, b]) => { setAll(a.data?.gruppi || []); setSel(new Set((b.data?.gruppi || []).map(g => g.id))); })
      .catch(() => {});
  }, [user.id]);
  const toggle = (id) => setSel(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const save = () => { setSaving(true); api.put(`/api/utenti/${user.id}/gruppi`, { gruppi: [...sel] }).then(() => { if (onSaved) onSaved(); onClose(); }).catch(() => setSaving(false)); };
  const list = all.filter(g => g.nome.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="nfx-dlg-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nfx-dlg" style={{ maxWidth: 600 }}>
        <div className="nfx-dlg__head"><h3>Gruppi di {user.nome_completo}</h3><button className="nfx-dlg__x" onClick={onClose}><X size={18} /></button></div>
        <div className="nfx-dlg__body">
          <div className="nfx-um-search"><Search size={16} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Cercare e aggiungere un gruppo" /></div>
          <div className="nfx-um-grouplist">
            {list.length === 0 && <div className="nfx-um-empty">Nessun gruppo. Creane uno dalla sezione Gruppi.</div>}
            {list.map(g => (
              <label key={g.id} className="nfx-um-groupitem">
                <input type="checkbox" checked={sel.has(g.id)} onChange={() => toggle(g.id)} />
                <GroupIcon icona={g.icona} colore={g.colore} size={24} />{g.nome}
              </label>
            ))}
          </div>
        </div>
        <div className="nfx-dlg__foot"><button className="nfx-btn-grey" onClick={onClose}>Annullare</button><button className="nfx-btn-blue" disabled={saving} onClick={save}>{saving ? 'Salvataggio…' : 'Registrare'}</button></div>
      </div>
    </div>
  );
};

const ResetPwdModal = ({ user, onClose }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const send = () => {
    setSending(true); setErr('');
    api.post(`/api/utenti/${user.id}/reset-password`)
      .then(() => { setSent(true); })
      .catch(e => setErr(e.response?.data?.messaggio || 'Invio non riuscito.'))
      .finally(() => setSending(false));
  };
  return (
    <div className="nfx-dlg-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nfx-dlg" style={{ maxWidth: 600 }}>
        <button className="nfx-dlg__x nfx-um-x" onClick={onClose}><X size={18} /></button>
        <div className="nfx-dlg__body" style={{ paddingTop: 40 }}>
          <div className="nfx-um-titleill"><span className="nfx-um-titleill__ic" style={{ background: 'var(--nfx-blue)' }}><Lock size={24} /></span>
            <span>Stai per inviare una richiesta di reimpostazione della password a <b>{user.nome_completo}</b>.</span>
          </div>
          <span className="nfx-um-text">All'utente verrà inviata una e-mail al suo indirizzo <b>{user.email}</b> con l'invito a scegliere una nuova password.</span>
          {sent && <div className="nfx-dlg__nota" style={{ color: '#2C8736', background: '#e8f7ea', borderColor: '#c5ecca' }}>E-mail di reimpostazione inviata a {user.email}.</div>}
          {err && <div className="nfx-dlg__nota" style={{ color: '#d71e04', background: '#ffe9e6', borderColor: '#f5c6c0' }}>{err}</div>}
        </div>
        <div className="nfx-dlg__foot">
          <button className="nfx-btn-grey" onClick={onClose}>{sent ? 'Chiudi' : 'Annullare'}</button>
          {!sent && <button className="nfx-btn-blue" onClick={send} disabled={sending}>{sending ? 'Invio…' : 'Invia'}</button>}
        </div>
      </div>
    </div>
  );
};

const DeleteUserModal = ({ user, onClose, onDeleted }) => {
  const pending = user.stato === 'in_attesa';
  const [saving, setSaving] = useState(false);
  const del = () => { setSaving(true); api.delete(`/api/utenti/${user.id}`).then(() => { if (onDeleted) onDeleted(); onClose(); }).catch(() => setSaving(false)); };
  return (
    <div className="nfx-dlg-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nfx-dlg" style={{ maxWidth: 600 }}>
        <button className="nfx-dlg__x nfx-um-x" onClick={onClose}><X size={18} /></button>
        <div className="nfx-dlg__body" style={{ paddingTop: 40 }}>
          <div className="nfx-um-titleill"><span className="nfx-um-titleill__ic"><AlertTriangle size={24} /></span>
            {pending
              ? <span>Stai per cancellare l'invito inviato a <b>{user.email}</b>.</span>
              : <span>Stai per <b>rimuovere {user.nome_completo} dall'organizzazione.</b></span>}
          </div>
          <ul className="nfx-um-list">
            {pending
              ? <><li>L'invito inviato a {user.email} <b>non sarà più valido</b>.</li><li>I diritti assegnati <b>verranno eliminati</b>.</li></>
              : <><li>L'utente <b>non potrà più accedere</b> all'organizzazione.</li><li>I diritti e le appartenenze ai gruppi <b>verranno eliminati</b>.</li></>}
          </ul>
        </div>
        <div className="nfx-dlg__foot"><button className="nfx-btn-grey" onClick={onClose}>Annullare</button><button className="nfx-um-btn-danger" disabled={saving} onClick={del}>{saving ? 'Eliminazione…' : 'Eliminare'}</button></div>
      </div>
    </div>
  );
};

const ProfileModal = ({ user, onClose, onSaved }) => {
  const [funzione, setFunzione] = useState(user.funzione || '');
  const [presentazione, setPresentazione] = useState(user.presentazione || '');
  const [saving, setSaving] = useState(false);
  const save = () => {
    setSaving(true);
    api.patch(`/api/utenti/${user.id}`, { funzione, presentazione })
      .then(() => { if (onSaved) onSaved(); onClose(); })
      .catch(() => setSaving(false));
  };
  return (
    <div className="nfx-dlg-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nfx-dlg" style={{ maxWidth: 490 }}>
        <div className="nfx-dlg__head"><h3>Modifica la funzione e l'aspetto di {user.nome_completo}</h3><button className="nfx-dlg__x" onClick={onClose}><X size={18} /></button></div>
        <div className="nfx-dlg__body">
          <label className="nfx-gr-field"><input value={funzione} onChange={e => setFunzione(e.target.value)} placeholder="Funzione (es. Responsabile marketing)" /></label>
          <label className="nfx-gr-field" style={{ height: 90, alignItems: 'flex-start', padding: '12px' }}><textarea value={presentazione} onChange={e => setPresentazione(e.target.value)} placeholder="Presentazione" style={{ width: '100%', border: 0, outline: 0, background: 'none', font: 'inherit', fontSize: 14, resize: 'vertical', minHeight: 60, color: 'var(--nfx-title)' }} /></label>
        </div>
        <div className="nfx-dlg__foot" style={{ justifyContent: 'flex-end' }}>
          <button className="nfx-btn-grey" onClick={onClose}>Annullare</button>
          <button className="nfx-btn-blue" disabled={saving} onClick={save}>{saving ? 'Salvataggio…' : 'Registrare'}</button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ 0c. SCHEDA UTENTE ("Gestisci l'utente") ═══════════════════ */
export const UtentiDettaglioPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [gruppi, setGruppi] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'role' | 'groups' | 'reset' | 'delete'
  const [inviteOpen, setInviteOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteMail, setInviteMail] = useState('');   // '' | 'sending' | 'sent' | 'err'
  const [alertMail, setAlertMail] = useState('');     // '' | 'sending' | 'sent' | 'err'

  const resendInvite = () => {
    setInviteMail('sending');
    api.post(`/api/utenti/${id}/rinvia-invito`).then(() => setInviteMail('sent')).catch(() => setInviteMail('err'));
  };
  const sendSecurityAlert = () => {
    setAlertMail('sending');
    api.post(`/api/utenti/${id}/avviso-sicurezza`).then(() => setAlertMail('sent')).catch(() => setAlertMail('err'));
  };

  const loadGroups = () => api.get(`/api/utenti/${id}/gruppi`).then(r => setGruppi(r.data?.gruppi || [])).catch(() => setGruppi([]));
  const loadUser = () => api.get(`/api/utenti/${id}`).then(r => setUser(r.data)).catch(() => setUser(false));
  useEffect(() => { loadUser(); loadGroups(); /* eslint-disable-line */ }, [id]);

  if (user === false) return <div className="nfx-cb"><Crumb1 label="Utente" /><div className="nfx-card" style={{ padding: 40 }}>Utente non trovato.</div></div>;
  if (!user) return <div className="nfx-cb"><div className="nfx-loading">Caricamento…</div></div>;

  const goList = () => navigate(V2_BASE + '/account/utenti');
  const initials = initialsOf(user.nome_completo, user.email);
  const pending = user.stato === 'in_attesa';

  return (
    <div className="nfx-cb">
      <div className="nfx-ud-crumbrow">
        <div className="nfx-crumb">
          <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
          <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
          <button className="nfx-crumb__link" onClick={goList}>Gestione degli utenti</button>
          <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
          <span className="nfx-crumb__cur">{user.nome_completo}</span>
        </div>
        <div className="nfx-ud-quick">
          <a className="nfx-ud-qbtn" href={`mailto:${user.email}`} title="Scrivi"><Mail size={16} /></a>
          {user.telefono && <a className="nfx-ud-qbtn" href={`tel:${user.telefono}`} title="Chiama"><Phone size={16} /></a>}
        </div>
      </div>

      <header className="nfx-ud-head">
        <div className="nfx-ud-id">
          {pending ? <span className="nfx-ud-avatar nfx-ud-avatar--pending"><Send size={26} /></span> : <span className="nfx-ud-avatar">{initials}</span>}
          <div>
            <div className="nfx-ud-name">{user.nome_completo}</div>
            <div className="nfx-ud-role">{roleLabel(user.ruolo)} {!user.is_owner && <button className="nfx-ud-pen" onClick={() => setModal('role')}><Pencil size={14} /></button>}</div>
          </div>
        </div>
        {!pending && (
          <div className="nfx-ud-col"><span className="nfx-ud-lbl">Funzione e presentazione</span>
            <span className="nfx-ud-val">{user.funzione || user.presentazione || 'Nessuna presentazione'} <button className="nfx-ud-pen" onClick={() => setModal('profile')}><Pencil size={14} /></button></span>
          </div>
        )}
        <div className="nfx-ud-col"><span className="nfx-ud-lbl">Indirizzo e-mail</span><span className="nfx-ud-val">{user.email}</span></div>
        <div className="nfx-ud-col"><span className="nfx-ud-lbl">Telefono</span><span className="nfx-ud-val">{user.telefono ? `${user.prefisso_telefono || ''} ${user.telefono}` : '—'}</span></div>
        <span className="nfx-tb__spacer" />
        <div className="nfx-ud-manage">
          <button className="nfx-btn-blue" onClick={() => setManageOpen(v => !v)}>Gestire <ChevronDown size={12} /></button>
          {manageOpen && (
            <div className="nfx-ul-ctx" style={{ right: 0 }} onMouseLeave={() => setManageOpen(false)}>
              <button className="nfx-ul-ctx-item" onClick={() => { setManageOpen(false); setModal('reset'); }}><Lock size={16} />Reimposta la password</button>
              <button className="nfx-ul-ctx-item" onClick={() => { setManageOpen(false); navigate(V2_BASE + '/account/rapporto-attivita'); }}><ClipboardList size={16} />Controlla l'attività</button>
              {!user.is_owner
                ? <button className="nfx-ul-ctx-item nfx-ul-ctx-item--danger" onClick={() => { setManageOpen(false); setModal('delete'); }}><Trash2 size={16} />{pending ? 'Eliminare l\'invito' : 'Rimuovi dall\'organizzazione'}</button>
                : <button className="nfx-ul-ctx-item nfx-ul-ctx-item--disabled" disabled><Trash2 size={16} />Rimuovi dall'organizzazione</button>}
            </div>
          )}
        </div>
      </header>

      {pending && (
        <section className={`nfx-pb${inviteOpen ? ' is-open' : ''}`}>
          <div className="nfx-pb-row">
            <div className="nfx-pb-left">
              <Info size={16} />
              <div className="nfx-pb-text">
                <span className="nfx-pb-title">Invito in attesa</span>
                <span className="nfx-pb-desc">L'utente non ha ancora accettato l'invito{user.creato_il ? ` inviato il ${new Date(user.creato_il).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}` : ''}.</span>
                <button className="nfx-pb-link" onClick={() => setInviteOpen(v => !v)}>Condividi il link di invito <ChevronDown size={14} /></button>
              </div>
            </div>
            <div className="nfx-pb-right">
              <button className="nfx-um-btn-danger" onClick={() => setModal('delete')}><Trash2 size={16} />Eliminare</button>
            </div>
          </div>
          {inviteOpen && (
            <div className="nfx-pb-more">
              <div className="nfx-pb-copyrow">
                <input readOnly value={`https://welcome.neoflux.it/signup?email=${encodeURIComponent(user.email)}`} />
                <button className="nfx-pb-copybtn" onClick={() => { navigator.clipboard?.writeText(`https://welcome.neoflux.it/signup?email=${encodeURIComponent(user.email)}`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                  <ClipboardList size={16} />{copied ? 'Copiato!' : 'Copiare il link'}
                </button>
              </div>
              <span className="nfx-um-text">Condividi questo link con l'utente, oppure invia direttamente l'invito via e-mail.</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="nfx-btn-blue" disabled={inviteMail === 'sending'} onClick={resendInvite}>
                  <Send size={16} style={{ marginRight: 6 }} />{inviteMail === 'sending' ? 'Invio…' : 'Invia per e-mail'}
                </button>
                {inviteMail === 'sent' && <span style={{ color: '#2C8736', fontSize: 13 }}>Invito inviato a {user.email}.</span>}
                {inviteMail === 'err' && <span style={{ color: '#d71e04', fontSize: 13 }}>Invio non riuscito.</span>}
              </div>
            </div>
          )}
        </section>
      )}

      <div className="nfx-ud-inforow">
        <div className="nfx-ud-infocard nfx-ud-infocard--teams">
          <div className="nfx-ud-infotitle">Gruppi</div>
          {gruppi.length === 0
            ? <button className="nfx-ud-link" onClick={() => setModal('groups')}><PlusCircle size={16} />Aggiungere un gruppo</button>
            : <div className="nfx-ud-grouptags">
                {gruppi.map(g => <span key={g.id} className="nfx-ud-grouptag"><GroupIcon icona={g.icona} colore={g.colore} size={22} />{g.nome}</span>)}
                <button className="nfx-ud-link" onClick={() => setModal('groups')}><PlusCircle size={16} />Modifica</button>
              </div>}
        </div>
        {pending ? (
          <div className="nfx-ud-infocard nfx-ud-infocard--activity" style={{ display: 'block' }}>
            <div className="nfx-ud-infotitle" style={{ marginBottom: 8 }}>I dati di accesso e sicurezza non sono ancora disponibili</div>
            <span className="nfx-ud-val">Una volta che l'utente ha accettato l'invito, potrai gestire la sicurezza del suo account.</span>
          </div>
        ) : (
          <div className="nfx-ud-infocard nfx-ud-infocard--activity">
            <div className="nfx-ud-half">
              <div className="nfx-ud-infotitle" style={{ marginBottom: 12 }}>Ultimo accesso</div>
              {user.ultimo_accesso
                ? <div className="nfx-ud-session"><span className="nfx-ud-os"><Monitor size={16} /></span><div><div className="nfx-ud-sesdate">{new Date(user.ultimo_accesso).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div></div></div>
                : <span className="nfx-ud-val">Nessun accesso registrato</span>}
            </div>
            <div className="nfx-ud-half nfx-ud-half--sec">
              <div className="nfx-ud-sechead">
                <div className="nfx-ud-infotitle" style={{ margin: 0 }}>Sicurezza</div>
                {!user.is_owner && (alertMail === 'sent'
                  ? <span style={{ color: '#2C8736', fontSize: 13 }}>Avviso inviato</span>
                  : <button className="nfx-ud-link" disabled={alertMail === 'sending'} onClick={sendSecurityAlert}>{alertMail === 'sending' ? 'Invio…' : 'Invia un avviso di sicurezza'}</button>)}
              </div>
              <div className="nfx-ud-security">
                <span className="nfx-ud-ring" style={{ background: 'conic-gradient(var(--nfx-orange) 40%, var(--nfx-border) 0)' }}><span>40%</span></span>
                <div><div className="nfx-ud-seclbl">La sicurezza dell'account è</div><div className="nfx-ud-secval">Accettabile</div></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <section className="nfx-ud-acc">
        <div className="nfx-ud-acc__head">
          <span className="nfx-ud-acc__ic"><Boxes size={24} /></span>
          <div><div className="nfx-ud-acc__title">Amministrazione del prodotto</div><div className="nfx-ud-acc__sub">Prodotti che questo utente può amministrare</div></div>
          <div className="nfx-ud-acc__end">
            {user.is_owner
              ? <span>Questo utente è <b style={{ color: '#009688' }}>{roleLabel(user.ruolo)}</b> · può accedere a <b>tutti i prodotti</b></span>
              : <span>Accesso ai soli prodotti autorizzati</span>}
          </div>
        </div>
      </section>

      {modal === 'role' && <RoleModal user={user} onClose={() => setModal(null)} onSaved={loadUser} />}
      {modal === 'profile' && <ProfileModal user={user} onClose={() => setModal(null)} onSaved={loadUser} />}
      {modal === 'groups' && <GroupsModal user={user} onClose={() => setModal(null)} onSaved={loadGroups} />}
      {modal === 'reset' && <ResetPwdModal user={user} onClose={() => setModal(null)} />}
      {modal === 'delete' && <DeleteUserModal user={user} onClose={() => setModal(null)} onDeleted={goList} />}
    </div>
  );
};

/* ═══════════════════ 0b. GRUPPI ═══════════════════ */
const GROUP_ICONS = [
  { key: 'users', Icon: Users2 }, { key: 'shield', Icon: Shield }, { key: 'lock', Icon: Lock },
  { key: 'briefcase', Icon: Briefcase }, { key: 'clock', Icon: Clock }, { key: 'zap', Icon: Zap },
  { key: 'globe', Icon: Globe },
];
const GROUP_COLORS = ['#d9364f', '#9C26B0', '#673AB7', '#4051B5', '#4BAF50', '#009688', '#00BCD4', '#02A9F4',
  '#2196F3', '#8BC34A', '#CDDC3A', '#FFC10A', '#FF9802', '#607D8B', '#9E9E9E', '#795548'];
const groupIconOf = (k) => (GROUP_ICONS.find(g => g.key === k) || GROUP_ICONS[0]).Icon;
const GroupIcon = ({ icona, colore, size = 32 }) => {
  const Ic = groupIconOf(icona);
  return <span className="nfx-gr-icon" style={{ '--c': colore, width: size, height: size }}><Ic size={Math.round(size * 0.56)} /></span>;
};

export const GruppiPage = () => {
  const [gruppi, setGruppi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [menuId, setMenuId] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [modal, setModal] = useState(null); // { nome, icona, colore, parentId, pickerOpen, pq, saving, err }

  const load = () => {
    setLoading(true);
    api.get('/api/gruppi').then(r => setGruppi(Array.isArray(r.data?.gruppi) ? r.data.gruppi : []))
      .catch(() => setGruppi([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const childrenOf = (pid) => gruppi.filter(g => (g.parent_id || null) === pid);
  const roots = childrenOf(null);
  const depthOf = (g) => { let d = 0; let p = g.parent_id; while (p) { const par = gruppi.find(x => x.id === p); if (!par) break; d++; p = par.parent_id; } return d; };
  const flatFiltered = gruppi.filter(g => g.nome.toLowerCase().includes(q.trim().toLowerCase()));

  const openCreate = (parentId = null) => setModal({ nome: '', icona: 'users', colore: '#4BAF50', parentId, pickerOpen: false, pq: '', saving: false, err: '' });
  const submitCreate = () => {
    const nome = modal.nome.trim();
    if (!nome) return;
    setModal(m => ({ ...m, saving: true, err: '' }));
    api.post('/api/gruppi', { nome, icona: modal.icona, colore: modal.colore, parent_id: modal.parentId })
      .then(() => { setModal(null); load(); })
      .catch(e => setModal(m => ({ ...m, saving: false, err: e.response?.data?.messaggio || 'Errore durante la creazione.' })));
  };
  const removeGroup = (g) => { setMenuId(null); api.delete(`/api/gruppi/${g.id}`).then(load).catch(() => {}); };

  const renderNode = (g, flat = false) => {
    const kids = flat ? [] : childrenOf(g.id);
    const isCollapsed = collapsed[g.id];
    return (
      <div key={g.id}>
        <div className="nfx-gr-item">
          <span className="nfx-gr-expand">
            {kids.length > 0 && <button onClick={() => setCollapsed(c => ({ ...c, [g.id]: !c[g.id] }))} aria-label="Espandi"><ChevronDown size={12} style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .12s' }} /></button>}
          </span>
          <span className="nfx-gr-drag" title="Trascina"><GripVertical size={18} /></span>
          <GroupIcon icona={g.icona} colore={g.colore} />
          <span className="nfx-gr-name">{g.nome}</span>
          <span className="nfx-gr-members" />
          <span className="nfx-gr-actions">
            <button className="nfx-gr-more" onClick={() => setMenuId(menuId === g.id ? null : g.id)} aria-label="Azioni"><MoreVertical size={20} /></button>
            {menuId === g.id && (
              <div className="nfx-gr-ctx" onMouseLeave={() => setMenuId(null)}>
                <div className="nfx-gr-ctx__head"><GroupIcon icona={g.icona} colore={g.colore} /><span className="nfx-gr-ctx__name">{g.nome}</span><button className="nfx-gr-ctx__edit" aria-label="Rinomina"><Pencil size={16} /></button></div>
                <div className="nfx-gr-ctx__list">
                  <button className="nfx-gr-ctx__item"><Settings size={16} />Gestisci il gruppo</button>
                  <button className="nfx-gr-ctx__item"><UserPlus size={16} />Aggiungi i membri</button>
                  <button className="nfx-gr-ctx__item" onClick={() => { setMenuId(null); openCreate(g.id); }}><FolderPlus size={16} />Crea un sottogruppo</button>
                  <button className="nfx-gr-ctx__item"><Move size={16} />Spostare il gruppo</button>
                  <button className="nfx-gr-ctx__item nfx-gr-ctx__item--danger" onClick={() => removeGroup(g)}><Trash2 size={16} />Eliminare il gruppo</button>
                </div>
              </div>
            )}
          </span>
        </div>
        {kids.length > 0 && !isCollapsed && <div className="nfx-gr-children">{kids.map(k => renderNode(k))}</div>}
      </div>
    );
  };

  return (
    <div className="nfx-cb">
      <Crumb1 label="Gruppi" />
      <div className="nfx-gr-card">
        <div className="nfx-gr-controls">
          <span className="nfx-tb__count"><b>{gruppi.length}</b> {gruppi.length === 1 ? 'Gruppo' : 'Gruppi'}</span>
          <button className="nfx-btn-blue nfx-gr-createbtn" onClick={() => openCreate(null)}><Plus size={16} />Crea un gruppo</button>
          <span className="nfx-tb__spacer" />
          <div className="nfx-gr-search"><Search size={14} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca un gruppo…" /></div>
          <button className="nfx-gr-obtn" title="Filtri"><SlidersHorizontal size={16} /></button>
        </div>

        {!loading && gruppi.length === 0 && (
          <div className="nfx-empty2" style={{ padding: '60px 0' }}><span className="nfx-empty2__icon"><Users2 size={40} /></span><div className="nfx-empty2__title">Nessun gruppo</div><div className="nfx-empty2__sub">Crea un gruppo per organizzare gli utenti e i loro permessi.</div></div>
        )}
        <div className="nfx-gr-tree">
          {q.trim() ? flatFiltered.map(g => renderNode(g, true)) : roots.map(g => renderNode(g))}
        </div>
      </div>

      {modal && (
        <div className="nfx-dlg-scrim nfx-gr-scrim" onMouseDown={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div className="nfx-gr-modal">
            <button className="nfx-gr-modal__x" onClick={() => setModal(null)} aria-label="Chiudi"><X size={16} /></button>
            <div className="nfx-gr-modal__body">
              <h3 className="nfx-gr-modal__title">Crea un gruppo</h3>
              <p className="nfx-gr-modal__sub">Puoi personalizzare l'icona e il colore del tuo gruppo.</p>
              <div className="nfx-gr-modal__row">
                <div className="nfx-gr-picker">
                  <button className={`nfx-gr-picker__btn${modal.pickerOpen ? ' is-open' : ''}`} onClick={() => setModal(m => ({ ...m, pickerOpen: !m.pickerOpen }))}>
                    <GroupIcon icona={modal.icona} colore={modal.colore} />
                    <ChevronDown size={10} className="nfx-gr-picker__chev" />
                  </button>
                  {modal.pickerOpen && (
                    <div className="nfx-gr-pickpop" onMouseLeave={() => setModal(m => ({ ...m, pickerOpen: false }))}>
                      <span className="nfx-gr-pick-lbl">Icona</span>
                      <div className="nfx-gr-pick-grid">
                        {GROUP_ICONS.map(({ key, Icon }) => (
                          <button key={key} className={`nfx-gr-pick-icon${modal.icona === key ? ' is-selected' : ''}`} onClick={() => setModal(m => ({ ...m, icona: key }))}><Icon size={16} /></button>
                        ))}
                      </div>
                      <span className="nfx-gr-pick-lbl">Colore</span>
                      <div className="nfx-gr-pick-grid" style={{ marginBottom: 0 }}>
                        {GROUP_COLORS.map(c => (
                          <button key={c} className="nfx-gr-pick-color" style={{ background: c }} onClick={() => setModal(m => ({ ...m, colore: c }))}>{modal.colore === c && <Check size={12} />}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <label className="nfx-gr-field"><input autoFocus value={modal.nome} onChange={e => setModal(m => ({ ...m, nome: e.target.value }))} placeholder="Amministrazione, Risorse umane…" /></label>
              </div>
              <div className="nfx-gr-parent">
                <label className="nfx-gr-parent__lbl">Crea il gruppo in:</label>
                <div className="nfx-gr-parent__search"><input value={modal.pq} onChange={e => setModal(m => ({ ...m, pq: e.target.value }))} placeholder="Seleziona una cartella o digita il nome di un gruppo" /></div>
                <div className="nfx-gr-parent__list">
                  <button className={`nfx-gr-parent__item${modal.parentId === null ? ' is-selected' : ''}`} onClick={() => setModal(m => ({ ...m, parentId: null }))}>
                    <span className="nfx-gr-parent__root"><Home size={16} /></span>Nessuno (gruppo principale)
                  </button>
                  {gruppi.filter(g => g.nome.toLowerCase().includes((modal.pq || '').toLowerCase())).map(g => (
                    <button key={g.id} className={`nfx-gr-parent__item${modal.parentId === g.id ? ' is-selected' : ''}`} style={{ paddingLeft: 12 + depthOf(g) * 20 }} onClick={() => setModal(m => ({ ...m, parentId: g.id }))}>
                      <GroupIcon icona={g.icona} colore={g.colore} size={24} />{g.nome}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="nfx-gr-modal__foot">
              {modal.err && <span className="nfx-gr-modal__err">{modal.err}</span>}
              <button className="nfx-btn-grey" onClick={() => setModal(null)}>Annullare</button>
              <button className="nfx-btn-blue" disabled={!modal.nome.trim() || modal.saving} onClick={submitCreate}>{modal.saving ? 'Creazione…' : 'Crea il gruppo'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════ 0c. RAPPORTO DI ATTIVITÀ ═══════════════════ */
export const RapportoAttivitaPage = () => {
  const { utente } = useAuth();
  const [attivita, loading] = useFetch('/api/attivita', null, []);
  const [profilo] = useFetch('/api/profilo', null, null);
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const acts = Array.isArray(attivita) ? attivita : [];
  const name = profilo?.nome_completo || utente?.nome_completo || utente?.email || 'Utente';
  const initials = initialsOf(profilo?.nome_completo || name, utente?.email);

  const pages = Math.max(1, Math.ceil(acts.length / perPage));
  const pageActs = acts.slice(page * perPage, page * perPage + perPage);
  const fmtDay = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

  return (
    <div className="nfx-cb">
      <Crumb1 label="Rapporto di attività" />
      <header className="nfx-ra-head">
        <div className="nfx-ra-title">Cronologia delle attività</div>
        <div className="nfx-ra-sub">Visualizza fino a un anno di cronologia degli accessi degli utenti alla tua organizzazione.</div>
      </header>
      <div className="nfx-card">
        <div className="nfx-tb">
          <span className="nfx-tb__count"><b>{acts.length}</b> registri</span>
          <button className="nfx-ra-export" title="Esporta"><Download size={16} /></button>
          <span className="nfx-tb__spacer" />
          <div className="nfx-tb__actions"><button className="nfx-iconbtn"><List size={18} /></button><button className="nfx-iconbtn"><Search size={18} /></button></div>
        </div>
        {loading ? <div className="nfx-loading">Caricamento…</div> : acts.length === 0 ? (
          <div className="nfx-empty2"><span className="nfx-empty2__icon"><Clock size={40} /></span><div className="nfx-empty2__title">Nessuna attività registrata</div></div>
        ) : (
          <table className="nfx-table nfx-ra-table">
            <thead><tr>
              <th>Nome</th><th>Azione</th>
              <th><span className="nfx-th-s"><ChevronsUpDown size={13} /> Data</span></th>
              <th>Prodotto</th><th>IP</th><th>Descrizione</th>
            </tr></thead>
            <tbody>{pageActs.map((a, i) => (
              <tr key={a.id || i}>
                <td><span className="nfx-cell-inline"><span className="nfx-ra-avatar">{initials}</span>{name}</span></td>
                <td>{a.tipo_attivita || '—'}</td>
                <td>{fmtDay(a.creato_il)}</td>
                <td>{a.metadata?.prodotto || 'n/a'}</td>
                <td>{a.ip_address || 'n/a'}</td>
                <td><span className="nfx-ra-desc">{a.descrizione || '—'}</span></td>
              </tr>
            ))}</tbody>
          </table>
        )}
        <div className="nfx-pager nfx-ra-pager">
          <div className="nfx-pager__left"><span>Risultati per pagina</span>
            <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(0); }}>
              <option>25</option><option>50</option><option>100</option>
            </select>
          </div>
          <span className="nfx-tb__spacer" />
          <span className="nfx-ra-pages">Pagina {page + 1} di {pages}</span>
          <button className="nfx-ra-nav" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} aria-label="Precedente"><ChevronLeft size={14} /></button>
          <button className="nfx-ra-nav" disabled={page >= pages - 1} onClick={() => setPage(p => Math.min(pages - 1, p + 1))} aria-label="Successiva"><ChevronRight size={14} /></button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ 1. IMPOSTAZIONI (dashboard) ═══════════════════ */
export const ImpostazioniAccountPage = () => {
  const { utente } = useAuth();
  const { t: tr, setLocale } = useLingua();
  const navigate = useNavigate();
  const go = (p) => navigate(V2_BASE + p);
  const [profilo] = useFetch('/api/profilo', null, null);
  const [sicurezza] = useFetch('/api/sicurezza', null, null);
  const [dispositivi] = useFetch('/api/dispositivi', null, []);
  const [prefsComm] = useFetch('/api/preferenze-comunicazione', null, null);

  const p = profilo || utente || {};
  const email = p.email || '';
  const [newsOn, setNewsOn] = useState(null); // override dopo salvataggio newsletter
  const newsSubscribed = newsOn !== null ? newsOn : !!prefsComm?.email_newsletter;
  const newsCount = newsSubscribed ? 1 : 0;
  const [ov, setOv] = useState(null); // override nome/cognome dopo modifica
  const baseName = p.nome_completo || [p.nome, p.cognome].filter(Boolean).join(' ') || p.email || 'Utente';
  const name = ov ? [ov.nome, ov.cognome].filter(Boolean).join(' ') : baseName;
  const initials = initialsOf(name, email);
  const has2fa = !!sicurezza?.autenticazione_due_fattori;
  const nDisp = Array.isArray(dispositivi) ? dispositivi.length : 0;
  const [emailModal, setEmailModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [delModal, setDelModal] = useState(false);
  const [orgModal, setOrgModal] = useState(false);
  const [newsModal, setNewsModal] = useState(false);
  const [prefsOv, setPrefsOv] = useState({});   // override lingua/nazione/timezone dopo salvataggio
  const [editField, setEditField] = useState(null); // 'lingua' | 'nazione' | 'timezone'
  const pl = { ...p, ...prefsOv };
  const saveField = (field, value) => {
    setPrefsOv((prev) => ({ ...prev, [field]: value }));
    setEditField(null);
    api.put('/api/profilo', { [field]: value }).catch(() => {});
  };
  const linguaLbl = (LINGUE.find((x) => x.v === (pl.lingua || 'it')) || LINGUE[0]).l;
  const paese = PAESI.find((x) => x.v === pl.nazione || x.l === pl.nazione) || PAESI[0];

  return (
    <div className="nfx-cb">
      <AccCrumb />
      {/* Header profilo */}
      <div className="nfx-acc-head">
        <div className="nfx-acc-id">
          <span className="nfx-acc-avatar" style={{ background: avatarColor(name || email) }}>{initials}</span>
          <div className="nfx-acc-nameline">
            <span className="nfx-acc-name">{name}</span>
            <button className="nfx-acc-nameic" aria-label="Modifica" onClick={() => setEditModal(true)}><Pencil size={17} /></button>
            <button className="nfx-acc-nameic" aria-label="Elimina" onClick={() => setDelModal(true)}><Trash2 size={17} /></button>
          </div>
        </div>
        <div className="nfx-acc-fields">
          <div>
            <div className="nfx-acc-field__label">Lingua</div>
            {editField === 'lingua' ? (
              <select className="nfx-acc-field__edit" autoFocus defaultValue={pl.lingua || 'it'}
                onChange={(e) => { saveField('lingua', e.target.value); setLocale(e.target.value); }} onBlur={() => setEditField(null)}>
                {LINGUE.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            ) : (
              <div className="nfx-acc-field__val">{linguaLbl} <button aria-label="Modifica lingua" onClick={() => setEditField('lingua')}><Pencil size={13} /></button></div>
            )}
          </div>
          <div>
            <div className="nfx-acc-field__label">Paese</div>
            {editField === 'nazione' ? (
              <select className="nfx-acc-field__edit" autoFocus defaultValue={paese.v}
                onChange={(e) => saveField('nazione', e.target.value)} onBlur={() => setEditField(null)}>
                {PAESI.map((o) => <option key={o.v} value={o.v}>{o.f} {o.l}</option>)}
              </select>
            ) : (
              <div className="nfx-acc-field__val">{paese.f} {paese.l} <button aria-label="Modifica paese" onClick={() => setEditField('nazione')}><Pencil size={13} /></button></div>
            )}
          </div>
          <div>
            <div className="nfx-acc-field__label">Fuso orario</div>
            {editField === 'timezone' ? (
              <select className="nfx-acc-field__edit" autoFocus defaultValue={pl.timezone || 'Europe/Rome'}
                onChange={(e) => saveField('timezone', e.target.value)} onBlur={() => setEditField(null)}>
                {FUSI.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <div className="nfx-acc-field__val">{pl.timezone || 'Europe/Rome'} <button aria-label="Modifica fuso orario" onClick={() => setEditField('timezone')}><Pencil size={13} /></button></div>
            )}
          </div>
          <div>
            <div className="nfx-acc-field__label">Documenti legali</div>
            <div className="nfx-acc-field__val"><a role="button" style={{ cursor: 'pointer' }} onClick={() => go('/settings/condizioni')}>Consultare</a></div>
          </div>
        </div>
      </div>

      {/* Cards: 4/3/3/2 del riferimento */}
      <div className="nfx-acc-grid">
        {/* Sicurezza e impostazioni di recupero */}
        <div className={`nfx-acc-card nfx-acc-card--sec${has2fa ? '' : ' nfx-acc-card--danger'}`}>
          <div className="nfx-acc-sec-title">
            <div className="nfx-acc-sec-title__l1">Sicurezza e impostazioni di recupero</div>
            <div className={`nfx-acc-sec-title__l2${has2fa ? ' is-ok' : ' is-low'}`}>{has2fa ? 'Ottima' : 'Critica'}</div>
          </div>
          <div className="nfx-acc-card__illu"><img src={`/assets/profile-security-${has2fa ? 'high' : 'low'}.svg`} alt="" /></div>
          {!has2fa && <div className="nfx-acc-sec-msg"><span className="nfx-acc-sec-msg__ic"><X size={12} /></span>Attiva la convalida in due fasi</div>}
          <div className="nfx-acc-sec-links">
            <button className="nfx-acc-sec-link" onClick={() => go('/account/sicurezza/2fa')}>
              <span className="nfx-acc-sec-link__l">
                <span className="nfx-acc-sec-link__circle"><Lock size={15} /></span>
                <span className="nfx-acc-sec-link__lines"><span>Convalida in due fasi</span></span>
              </span>
              <ChevronRight size={16} />
            </button>
            <span className="nfx-acc-sec-delim" />
            <button className="nfx-acc-sec-link" onClick={() => go('/account/sicurezza')}>
              <span className="nfx-acc-sec-link__l">
                <span className="nfx-acc-sec-link__circle"><Shield size={15} /></span>
                <span className="nfx-acc-sec-link__lines"><span>Password compromessa?</span><span className="nfx-acc-sec-link__sub">Proteggi i tuoi accessi</span></span>
              </span>
              <ChevronRight size={16} />
            </button>
          </div>
          {!has2fa && <button className="nfx-acc-btn-danger" onClick={() => go('/account/sicurezza')}>Correggi adesso</button>}
        </div>

        {/* Impostazioni di accesso */}
        <div className="nfx-acc-card">
          <div className="nfx-acc-card__title">Impostazioni di accesso</div>
          <div className="nfx-acc-card__illu"><img src="/assets/profile-configuration-connexion.svg" alt="" /></div>
          <div className="nfx-acc-conn">
            <div className="nfx-acc-conn__title">Indirizzo e-mail di connessione <Info size={14} className="nfx-acc-i" /></div>
            <div className="nfx-acc-conn__val">{email} <Check size={15} className="nfx-acc-ok" /></div>
            <button className="nfx-acc-link nfx-acc-selfstart" onClick={() => setEmailModal(true)}>Modificare</button>
          </div>
          <div className="nfx-acc-conn">
            <div className="nfx-acc-conn__title">Password di accesso</div>
            <div className="nfx-acc-conn__val">Modificato il {fmtDate(p.aggiornato_il)}</div>
            <button className="nfx-acc-link nfx-acc-selfstart" onClick={() => go('/account/password')}>Modificare</button>
          </div>
        </div>

        {/* Colonna 3: due card impilate */}
        <div className="nfx-acc-col">
          {/* Accesso e attività */}
          <div className="nfx-acc-card nfx-acc-card--grow">
            <div className="nfx-acc-card__title">Accesso e attività dell'account</div>
            <div className="nfx-acc-card__illu"><img src="/assets/profile-history-connexion.svg" alt="" /></div>
            <div className="nfx-acc-hist">
              <div className="nfx-acc-hist__nb"><b>{nDisp}</b> {nDisp === 1 ? 'dispositivo connesso' : 'dispositivi connessi'} a questo account</div>
              <button className="nfx-acc-link nfx-acc-selfstart" onClick={() => go('/account/accesso')}>Gestire</button>
            </div>
          </div>
          {/* Password dell'applicazione */}
          <div className="nfx-acc-card">
            <div className="nfx-acc-linkrow">
              <div className="nfx-acc-card__title">Password dell'applicazione</div>
              <button className="nfx-acc-link" onClick={() => go('/account/app-password')}>Gestire</button>
            </div>
            <div className="nfx-acc-row__v"><b>0</b>&nbsp;password dell'applicazione <Info size={14} className="nfx-acc-i" /></div>
          </div>
        </div>

        {/* Supporto */}
        <div className="nfx-acc-card nfx-acc-card--info nfx-acc-sup">
          <div className="nfx-acc-card__title">Supporto <Info size={14} className="nfx-acc-i" /></div>
          <div className="nfx-acc-card__illu"><img src="/assets/profile-support.svg" alt="" /></div>
          <div className="nfx-acc-sup__st">Autorizzazione di accesso</div>
          <div className="nfx-acc-sup__nb"><b>0</b> autorizzazione</div>
          <button className="nfx-acc-link nfx-acc-selfstart" onClick={() => go('/account/supporto')}>Gestire</button>
          <div className="nfx-acc-sup__st">Codice supporto</div>
          <SupportCode variant="card" />
        </div>
      </div>

      {/* secondary */}
      <div className="nfx-acc-grid2">
        <div className="nfx-acc-card">
          <div className="nfx-acc-linkrow"><div className="nfx-acc-card__title">Le mie organizzazioni</div><button className="nfx-acc-link" onClick={() => setOrgModal(true)}>Gestire</button></div>
          <div className="nfx-acc-row__v"><b>1</b> organizzazione</div>
        </div>
        <div className="nfx-acc-card">
          <div className="nfx-acc-linkrow"><div className="nfx-acc-card__title">News NeoFlux</div><button className="nfx-acc-link" onClick={() => setNewsModal(true)}>Gestire</button></div>
          <div className="nfx-acc-row__v"><b>{newsCount}</b> abbonamento(i)</div>
        </div>
      </div>

      {/* Applicazioni */}
      <div className="nfx-acc-bar">
        <div className="nfx-acc-bar__title">{tr('Applicazioni')} <span className="nfx-acc-bar__badge">0</span></div>
        <div className="nfx-acc-bar__desc">{tr('Consulta gli accessi alle tue applicazioni')}</div>
      </div>

      {/* Sviluppatori */}
      <div className="nfx-acc-bar">
        <div className="nfx-acc-bar__title" style={{ color: 'var(--nfx-purple)' }}><Boxes size={17} /> {tr('API NeoFlux')}</div>
        <a className="nfx-acc-link" href="#docs" onClick={e => e.preventDefault()}>{tr('Leggi la documentazione per gli sviluppatori')}</a>
        <span className="nfx-tb__spacer" style={{ flex: 1 }} />
        <div className="nfx-acc-bar__desc">{tr('0 token API')} <button className="nfx-acc-link" onClick={() => go('/account/sviluppatore/token')}>{tr('Gestire')}</button></div>
      </div>

      {emailModal && <EmailConnModal email={email} onClose={() => setEmailModal(false)} />}
      {editModal && (
        <EditPersonalModal
          nome={ov?.nome ?? p.nome ?? ''}
          cognome={ov?.cognome ?? p.cognome ?? ''}
          onClose={(saved) => { if (saved) setOv(saved); setEditModal(false); }}
        />
      )}
      {delModal && <DeleteAccountModal name={name} onClose={() => setDelModal(false)} />}
      {orgModal && <OrganizationsModal orgName={p.ragione_sociale || name} initials={initials} onClose={() => setOrgModal(false)} />}
      {newsModal && <NewsletterModal onClose={(v) => { if (typeof v === 'boolean') setNewsOn(v); setNewsModal(false); }} />}
    </div>
  );
};

/* ═══════════════════ 2. GESTIONE PASSWORD ═══════════════════ */
export const PasswordPage = () => {
  const { utente } = useAuth();
  const [profilo] = useFetch('/api/profilo', null, null);
  const email = profilo?.email || utente?.email || '';
  const initials = initialsOf(profilo?.nome_completo, email);
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [conf, setConf] = useState('');
  const [show, setShow] = useState({});
  const reqs = { low: /[a-z]/.test(nw), up: /[A-Z]/.test(nw), num: /[0-9]/.test(nw), len: nw.length >= 8 };
  const valid = cur && reqs.low && reqs.up && reqs.num && reqs.len && nw === conf;
  return (
    <div className="nfx-cb">
      <AccCrumb current="Gestione delle password" />
      <div className="nfx-card">
        <div className="nfx-pwd">
          <div className="nfx-pwd__title">Modifica della tua password di accesso</div>
          <div className="nfx-pwd__avatar">{initials}</div>
          <div className="nfx-pwd__email">{email}</div>

          <div className={`nfx-pwd__field${cur === '' ? ' is-error' : ''}`}>
            <input type={show.cur ? 'text' : 'password'} placeholder="Password aggiornata *" value={cur} onChange={e => setCur(e.target.value)} />
            <button onClick={() => setShow(s => ({ ...s, cur: !s.cur }))}>{show.cur ? <EyeOff size={18} /> : <Eye size={18} />}</button>
          </div>

          <div className="nfx-pwd__q">Quale sarà la tua nuova password?</div>
          <div className="nfx-pwd__field">
            <input type={show.nw ? 'text' : 'password'} placeholder="Nuova password *" value={nw} onChange={e => setNw(e.target.value)} />
            <button onClick={() => setShow(s => ({ ...s, nw: !s.nw }))}>{show.nw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            <button onClick={() => setNw('')}><RotateCcw size={16} /></button>
          </div>
          <div className="nfx-pwd__reqs">
            <div className="nfx-pwd__req"><div className={`nfx-pwd__req-b${reqs.low ? ' ok' : ''}`}>a</div><div className="nfx-pwd__req-l">Minuscolo</div></div>
            <div className="nfx-pwd__req"><div className={`nfx-pwd__req-b${reqs.up ? ' ok' : ''}`}>A</div><div className="nfx-pwd__req-l">Maiuscolo</div></div>
            <div className="nfx-pwd__req"><div className={`nfx-pwd__req-b${reqs.num ? ' ok' : ''}`}>0-9</div><div className="nfx-pwd__req-l">Numero</div></div>
            <div className="nfx-pwd__req"><div className={`nfx-pwd__req-b${reqs.len ? ' ok' : ''}`}>8+</div><div className="nfx-pwd__req-l">Caratteri</div></div>
          </div>
          <div className={`nfx-pwd__field${conf && conf !== nw ? ' is-error' : ''}`}>
            <input type={show.conf ? 'text' : 'password'} placeholder="Conferma della nuova password *" value={conf} onChange={e => setConf(e.target.value)} />
            <button onClick={() => setShow(s => ({ ...s, conf: !s.conf }))}>{show.conf ? <EyeOff size={18} /> : <Eye size={18} />}</button>
          </div>
          <button className={`nfx-pwd__submit${valid ? '' : ' is-disabled'}`} disabled={!valid}>Salva la nuova password</button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ 3. PASSWORD DELL'APPLICAZIONE ═══════════════════ */
export const AppPasswordPage = () => (
  <div className="nfx-cb">
    <AccCrumb current="Password dell'applicazione" />
    <h1 className="nfx-cb-title">Password dell'applicazione</h1>
    <div className="nfx-info-banner">
      <Info size={18} /> Adesso, ogni dispositivo ha la propria password.
      <a href="#i" onClick={e => e.preventDefault()}>Per più informazioni</a>
    </div>
    <div className="nfx-tb" style={{ padding: '4px 0 12px' }}>
      <span className="nfx-tb__count"><b>0</b> password dell'applicazione</span>
      <div className="nfx-search-field" style={{ marginLeft: 12, maxWidth: 320 }}><Search size={16} /><input placeholder="Cerca una password" /></div>
      <span className="nfx-tb__spacer" />
      <div className="nfx-tb__actions"><button className="nfx-iconbtn"><SlidersHorizontal size={18} /></button><button className="nfx-iconbtn is-active"><List size={18} /></button><button className="nfx-iconbtn"><LayoutGrid size={18} /></button></div>
    </div>
    <button className="nfx-addfilter" style={{ marginBottom: 16 }}><Plus size={15} /> Crea una password dell'applicazione</button>
    <div className="nfx-card">
      <div className="nfx-token-empty">
        <div className="nfx-token-empty__circle" style={{ background: 'var(--nfx-blue)', color: '#fff' }}><KeyRound size={40} /></div>
        <div className="nfx-token-empty__text">Nessuna password dell'applicazione</div>
        <div className="nfx-empty2__sub">Non hai ancora creato la password delle tue applicazioni</div>
        <button className="nfx-addfilter"><Plus size={15} /> Crea una password dell'applicazione</button>
      </div>
    </div>
  </div>
);

/* ═══════════════════ 4. ACCESSO E ATTIVITÀ ═══════════════════ */
export const AccessoPage = () => {
  const [tab, setTab] = useState('sessioni');
  const [dispositivi, loadD] = useFetch('/api/dispositivi', null, []);
  const [attivita, loadA] = useFetch('/api/attivita', null, []);
  const disp = Array.isArray(dispositivi) ? dispositivi : [];
  const acts = Array.isArray(attivita) ? attivita : [];
  const dates = acts.map(a => a.creato_il).filter(Boolean).sort();
  const rangeStr = dates.length ? `${fmtDate(dates[0])}  ›  ${fmtDate(dates[dates.length - 1])}` : '—';

  return (
    <div className="nfx-cb">
      <AccCrumb current="Accesso e attività" />
      <h1 className="nfx-cb-title">Accesso e attività dell'account</h1>
      <p className="nfx-cb-intro">Questa pagina ti consente di verificare le sessioni attive e i dispositivi dai quali sei connesso al tuo account utente <a href="#i" onClick={e => e.preventDefault()}>Per più informazioni</a></p>

      <div className="nfx-acc2-tabs">
        <button className={`nfx-acc2-tab${tab === 'sessioni' ? ' is-active' : ''}`} onClick={() => setTab('sessioni')}>Accesso all'account</button>
        <button className={`nfx-acc2-tab${tab === 'attivita' ? ' is-active' : ''}`} onClick={() => setTab('attivita')}>Attività dell'account</button>
      </div>

      {tab === 'sessioni' ? (
        <>
          <div className="nfx-acc2-count">{disp.length} {disp.length === 1 ? 'sessione' : 'sessioni'}</div>
          <p className="nfx-acc2-help">Sei connesso al tuo account utente sulle sessioni seguenti. Se non riconosci nessuna di esse, <strong>ripristina la connessione disconnettendoti</strong>. Dovrai quindi rieffettuare l'accesso con le tue credenziali abituali.</p>
          <div className="nfx-acc2-toolbar">
            <div className="nfx-search-field"><Search size={16} /><input placeholder="Cerca una sessione" /></div>
            <span className="nfx-tb__spacer" />
            <div className="nfx-tb__actions"><button className="nfx-iconbtn is-active"><List size={18} /></button><button className="nfx-iconbtn"><LayoutGrid size={18} /></button></div>
          </div>
          {loadD ? <div className="nfx-loading">Caricamento…</div> : disp.length === 0 ? (
            <div className="nfx-empty2"><span className="nfx-empty2__icon"><Monitor size={40} /></span><div className="nfx-empty2__title">Nessuna sessione attiva</div></div>
          ) : (
            <table className="nfx-table nfx-acc2-table">
              <thead><tr><th>Dispositivo</th><th>Piattaforma</th><th>Posizione</th><th>Data di accesso</th><th className="nfx-td-actions" /></tr></thead>
              <tbody>{disp.map((d, i) => (
                <tr key={d.id || i}>
                  <td className="nfx-cell-strong">{d.sistema_operativo || d.nome_dispositivo || '—'}</td>
                  <td>{d.browser || '—'}</td>
                  <td>{d.posizione || d.localita || d.citta || d.ip_address || 'Sconosciuta'}</td>
                  <td><span className="nfx-cell-inline">{fmtDateTime(d.ultima_attivita)} <span className="nfx-acc2-dot" /></span></td>
                  <td className="nfx-td-actions"><button className="nfx-iconbtn" title="Disconnetti"><Unlink size={17} /></button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </>
      ) : (
        <>
          <div className="nfx-acc2-count">{acts.length} {acts.length === 1 ? 'attività' : 'attività'}</div>
          <p className="nfx-acc2-help">Controlla fino a un anno indietro la cronologia delle attività relative al tuo account utente. Controllate regolarmente la sicurezza del vostro <a href="#s" onClick={e => e.preventDefault()}>account</a> alla ricerca di attività sospette.</p>
          <div className="nfx-acc2-toolbar">
            <div className="nfx-search-field"><Search size={16} /><input placeholder="Cerca una sessione" /></div>
            <button className="nfx-acc2-filter"><Filter size={15} /> Filtra (<b>0</b>) <ChevronDown size={14} /></button>
            <div className="nfx-acc2-date"><Clock size={14} /> {rangeStr}</div>
            <span className="nfx-tb__spacer" />
            <div className="nfx-tb__actions"><button className="nfx-iconbtn is-active"><List size={18} /></button><button className="nfx-iconbtn"><LayoutGrid size={18} /></button></div>
          </div>
          {loadA ? <div className="nfx-loading">Caricamento…</div> : acts.length === 0 ? (
            <div className="nfx-empty2"><span className="nfx-empty2__icon"><Clock size={40} /></span><div className="nfx-empty2__title">Nessuna attività registrata</div></div>
          ) : (
            <table className="nfx-table nfx-acc2-table">
              <thead><tr><th>Azioni</th><th>Data</th><th>Prodotto</th><th>IP</th><th>Descrizioni</th></tr></thead>
              <tbody>{acts.map((a, i) => (
                <tr key={a.id || i}>
                  <td className="nfx-cell-strong">{a.tipo_attivita || '—'}</td>
                  <td>{fmtDate(a.creato_il)}</td>
                  <td>{a.metadata?.prodotto ? <span className="nfx-acc2-prod"><span className="nfx-acc2-prod__ic"><Package size={12} /></span>{a.metadata.prodotto}</span> : ''}</td>
                  <td>{a.ip_address || 'System'}</td>
                  <td className="nfx-cell-sub" style={{ marginTop: 0 }}>{a.descrizione || '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

/* ═══════════════════ 5. SICUREZZA ═══════════════════ */
/* Icona di stato (X rossa / check verde) */
const SecIco = ({ ok }) => (
  <span className={`nfx-secr-ico nfx-secr-ico--${ok ? 'ok' : 'danger'}`}>
    {ok ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
  </span>
);

export const SicurezzaPage = () => {
  const { utente } = useAuth();
  const navigate = useNavigate();
  const go = (p) => navigate(V2_BASE + p);
  const [profilo] = useFetch('/api/profilo', null, null);
  const [sicurezza] = useFetch('/api/sicurezza', null, null);
  const [dispositivi] = useFetch('/api/dispositivi', null, []);
  const [s, setS] = useState(null);
  const [emailModal, setEmailModal] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [recEmailModal, setRecEmailModal] = useState(false);
  const [phoneModal, setPhoneModal] = useState(false);
  useEffect(() => { setS(sicurezza); }, [sicurezza]);
  const save = (patch) => {
    const next = { ...(s || {}), ...patch };
    setS(next);
    api.put('/api/sicurezza', patch).catch(() => {});
  };
  const v = s || {};
  const p = profilo || utente || {};
  const has2fa = !!v.autenticazione_due_fattori;
  const nDisp = Array.isArray(dispositivi) ? dispositivi.length : 0;
  const email = p.email || '';
  const recEmail = v.email_recupero || '';
  const recPhone = v.telefono_recupero || '';
  const phoneOk = !!v.telefono_recupero_confermato;

  return (
    <div className="nfx-cb">
      <AccCrumb current="Sicurezza dell'account" />

      {/* Header */}
      <div className="nfx-secr-head">
        <div className="nfx-secr-head__l">
          <h2>Sicurezza dell'account</h2>
          {!has2fa && <div className="nfx-secr-head__badge">Critica</div>}
          <p>Proteggi il tuo account aggiungendo le <strong>informazioni mancanti.</strong></p>
        </div>
        <button className="nfx-secr-head__btn" onClick={() => setSettingsModal(true)}><Settings size={16} /> Impostazioni di sicurezza</button>
      </div>

      {/* Griglia card */}
      <div className="nfx-secr-wrap">
      <div className="nfx-secr-grid">
        {/* Convalida in due fasi */}
        <div className="nfx-secr-card">
          <div className="nfx-secr-card__main">
            <div className="nfx-secr-card__left">
              <SecIco ok={has2fa} />
              <div className="nfx-secr-card__info">
                <div className="nfx-secr-card__title">Convalida in due fasi <Info size={14} className="nfx-acc-i" /></div>
                {!has2fa && <div className="nfx-secr-card__val is-danger">Accresci la sicurezza del tuo account attivando la convalida in due fasi</div>}
                {has2fa && <div className="nfx-secr-card__val">Attiva</div>}
              </div>
            </div>
            <div className="nfx-secr-card__actions">
              {has2fa
                ? <button className="nfx-secr-link" onClick={() => save({ autenticazione_due_fattori: false })}>Disattiva</button>
                : <button className="nfx-secr-btn-danger" onClick={() => go('/account/sicurezza/2fa')}>Attiva adesso</button>}
            </div>
          </div>
        </div>

        {/* Dispositivi connessi */}
        <div className="nfx-secr-card">
          <div className="nfx-secr-card__main">
            <div className="nfx-secr-card__left">
              <div className="nfx-secr-card__info">
                <div className="nfx-secr-card__title">Dispositivi connessi</div>
                <div className="nfx-secr-card__val">{nDisp} {nDisp === 1 ? 'dispositivo connesso' : 'dispositivi connessi'}</div>
                <button className="nfx-secr-disc" onClick={() => go('/account/accesso')}>Disconnetti tutto</button>
              </div>
            </div>
            <div className="nfx-secr-card__actions">
              <button className="nfx-secr-link" onClick={() => go('/account/accesso')}>Gestire</button>
            </div>
          </div>
        </div>

        {/* Indirizzo e-mail di connessione */}
        <div className="nfx-secr-card">
          <div className="nfx-secr-card__main">
            <div className="nfx-secr-card__left">
              <SecIco ok />
              <div className="nfx-secr-card__info">
                <div className="nfx-secr-card__title">Indirizzo e-mail di connessione</div>
                <div className="nfx-secr-card__val">{email}</div>
              </div>
            </div>
            <div className="nfx-secr-card__actions">
              <button className="nfx-secr-link" onClick={() => setEmailModal(true)}>Modificare</button>
            </div>
          </div>
        </div>

        {/* Password di accesso */}
        <div className="nfx-secr-card">
          <div className="nfx-secr-card__main">
            <div className="nfx-secr-card__left">
              <SecIco ok />
              <div className="nfx-secr-card__info">
                <div className="nfx-secr-card__title">Password di accesso</div>
                <div className="nfx-secr-card__val">Modificato il&nbsp;{fmtDate(p.aggiornato_il)}</div>
              </div>
            </div>
            <div className="nfx-secr-card__actions">
              <button className="nfx-secr-link" onClick={() => go('/account/password')}>Modificare</button>
            </div>
          </div>
        </div>

        {/* Indirizzo e-mail di recupero */}
        <div className="nfx-secr-card">
          <div className="nfx-secr-card__main">
            <div className="nfx-secr-card__left">
              <SecIco ok={!!recEmail} />
              <div className="nfx-secr-card__info">
                <div className="nfx-secr-card__title">Indirizzo e-mail di recupero</div>
                {recEmail
                  ? <div className="nfx-secr-card__val">{recEmail}</div>
                  : <div className="nfx-secr-card__val is-danger">Questa informazione è essenziale se dimentichi la password.</div>}
              </div>
            </div>
            <div className="nfx-secr-card__actions">
              <button className="nfx-secr-link" onClick={() => setRecEmailModal(true)}>{recEmail ? 'Modificare' : 'Aggiungi'}</button>
            </div>
          </div>
        </div>

        {/* Telefono di recupero */}
        <div className="nfx-secr-card">
          <div className="nfx-secr-card__main">
            <div className="nfx-secr-card__left">
              <SecIco ok={!!recPhone && phoneOk} />
              <div className="nfx-secr-card__info">
                <div className="nfx-secr-card__title">Telefono di recupero</div>
                {recPhone && phoneOk && <div className="nfx-secr-card__val">{recPhone}</div>}
              </div>
            </div>
            <div className="nfx-secr-card__actions">
              <button className="nfx-secr-link" onClick={() => setPhoneModal(true)}>Modificare</button>
            </div>
          </div>
          {recPhone && !phoneOk && (
            <div className="nfx-secr-sub">
              <div className="nfx-secr-sub__l">
                <AlertTriangle size={16} color="#d9364f" style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div className="nfx-secr-card__val" style={{ color: 'var(--nfx-title)' }}>{recPhone}</div>
                  <button className="nfx-secr-link" onClick={() => save({ telefono_recupero_confermato: true })}>Conferma il numero</button>
                </div>
              </div>
              <button className="nfx-secr-link" onClick={() => save({ telefono_recupero: '' })}>Eliminare</button>
            </div>
          )}
        </div>
      </div>

      {/* Illustrazione (fluttua in basso a destra) */}
      <div className="nfx-secr-illu"><img src={`/assets/profile-security-${has2fa ? 'high' : 'low'}.svg`} alt="" /></div>
      </div>

      {emailModal && <EmailConnModal email={email} onClose={() => setEmailModal(false)} />}
      {settingsModal && <SecuritySettingsModal v={v} onSave={save} onClose={() => setSettingsModal(false)} />}
      {recEmailModal && <RecoveryEmailModal current={recEmail} onSave={(em) => save({ email_recupero: em })} onClose={() => setRecEmailModal(false)} />}
      {phoneModal && <PhoneRecoveryModal initial={recPhone} onSave={(ph) => save({ telefono_recupero: ph, telefono_recupero_confermato: false })} onClose={() => setPhoneModal(false)} />}
    </div>
  );
};

/* ── QR "finto" deterministico (per il mock OTP) ── */
const FakeQR = ({ seed, size = 176 }) => {
  const N = 25, cell = size / N;
  let h = 0; for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const rnd = (i) => { let x = (h ^ (i * 2654435761)) >>> 0; x ^= x << 13; x >>>= 0; x ^= x >> 17; x ^= x << 5; x >>>= 0; return (x % 100) / 100; };
  const finder = (r, c) => {
    const local = (R, C) => { const rr = r - R, cc = c - C; if (rr < 0 || cc < 0 || rr > 6 || cc > 6) return null; const b = rr === 0 || rr === 6 || cc === 0 || cc === 6; const ctr = rr >= 2 && rr <= 4 && cc >= 2 && cc <= 4; return b || ctr; };
    const a = local(0, 0); if (a !== null) return a;
    const b = local(0, N - 7); if (b !== null) return b;
    const d = local(N - 7, 0); if (d !== null) return d;
    return undefined;
  };
  const rects = [];
  for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
    const f = finder(r, c);
    const fill = f !== undefined ? f : rnd(r * N + c) > 0.55;
    if (fill) rects.push(<rect key={`${r}-${c}`} x={c * cell} y={r * cell} width={cell} height={cell} fill="#1a1e2b" />);
  }
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{rects}</svg>;
};

const B32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
const genSecret = (n) => Array.from({ length: n }, () => B32[Math.floor(Math.random() * 32)]).join('');
const genCode = () => String(Math.floor(10000000 + Math.random() * 90000000));

/* ═══════════════════ 5b. CONVALIDA IN DUE FASI (wizard) ═══════════════════ */
export const Doppia2faPage = () => {
  const navigate = useNavigate();
  const go = (p) => navigate(V2_BASE + p);
  const [step, setStep] = useState(1);
  const [sel, setSel] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [code, setCode] = useState('');
  const [flowId, setFlowId] = useState('');
  const [csrf, setCsrf] = useState('');
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [needsReauth, setNeedsReauth] = useState(false);

  // Avvia l'enrollment TOTP reale via Kratos (settings flow proxied dal backend)
  const beginTotp = async () => {
    setBusy(true); setErr(''); setNeedsReauth(false);
    try {
      const r = await api.get('/api/2fa/totp/begin');
      setFlowId(r.data.flowId); setSecret(r.data.secret || ''); setQr(r.data.qr || ''); setCsrf(r.data.csrf || '');
      setStep(2);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.data?.needsReauth) {
        setNeedsReauth(true); setErr('Per motivi di sicurezza devi effettuare di nuovo l’accesso prima di configurare la 2FA.');
      } else setErr('Impossibile avviare la configurazione. Riprova.');
    } finally { setBusy(false); }
  };
  // Conferma il codice TOTP e genera i codici di recupero reali
  const confirmTotp = async () => {
    setBusy(true); setErr('');
    try {
      await api.post('/api/2fa/totp/confirm', { flowId, code, csrf });
      try { const rc = await api.post('/api/2fa/recovery', {}); setCodes(Array.isArray(rc.data.codes) ? rc.data.codes : []); } catch { setCodes([]); }
      setStep(4);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.data?.needsReauth) {
        setNeedsReauth(true); setErr('Sessione scaduta: effettua di nuovo l’accesso e riprova.');
      } else setErr(e?.response?.data?.messaggio || 'Codice non valido. Riprova.');
    } finally { setBusy(false); }
  };
  const reauth = () => { window.location.href = `/auth/login?next=${encodeURIComponent('/account/sicurezza/2fa')}`; };

  const labels = ['Scelta del metodo di convalida', 'Configurazione', 'Verifica', "Codici d'emergenza"];

  const finish = () => {
    api.put('/api/sicurezza', { autenticazione_due_fattori: true }).catch(() => {});
    go('/account/sicurezza');
  };
  const downloadCodes = () => {
    try {
      const blob = new Blob([`Codici d'emergenza NeoFlux\n\n${codes.join('\n')}\n`], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'neoflux-codici-emergenza.txt'; a.click();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };

  const methods = [
    { key: 'otp', featured: true, title: 'Applicazione OTP', icon: <Smartphone size={24} />, desc: "Installa un'applicazione OTP di terzi (Google Authenticator, Authy…).", cta: 'Configurare' },
  ];

  return (
    <div className="nfx-cb">
      {/* Breadcrumb 4 livelli */}
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <button className="nfx-crumb__link" onClick={() => go('/account')}>Il mio profilo</button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <button className="nfx-crumb__link" onClick={() => go('/account/sicurezza')}>Sicurezza dell'account</button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <span className="nfx-crumb__cur">Convalida in due fasi</span>
      </div>

      {/* Stepper */}
      <div className="nfx-2fa-steps">
        {labels.map((lab, i) => (
          <div key={i} className={`nfx-2fa-step${i + 1 <= step ? ' is-active' : ''}`}>
            {i + 1 === step && <span className="nfx-2fa-step__label">{lab}</span>}
            <span className="nfx-2fa-step__bar" />
          </div>
        ))}
      </div>

      <div className="nfx-card nfx-2fa-card">
        {err && (
          <div style={{ margin: '0 0 16px', padding: '10px 14px', borderRadius: 10, background: 'var(--nfx-danger-soft)', color: 'var(--nfx-danger-fg)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>{err}</span>
            {needsReauth && <button className="nfx-btn-sec" onClick={reauth}>Accedi di nuovo</button>}
          </div>
        )}
        {/* STEP 1 — scelta metodo */}
        {step === 1 && (
          <>
            <h2 className="nfx-2fa-title">Come vuoi convalidare le tue connessioni?</h2>
            <p className="nfx-2fa-sub">La convalida in due fasi protegge il tuo account NeoFlux dagli hacker, anche se sono in possesso della tua password.</p>
            <div className="nfx-2fa-methods">
              {methods.map(m => (
                <div key={m.key} className={`nfx-2fa-method${m.featured ? ' is-featured' : ''}${sel === m.key ? ' is-sel' : ''}`} onClick={() => setSel(m.key)}>
                  {m.featured && <span className="nfx-2fa-ribbon"><Star size={12} fill="#fff" strokeWidth={0} /></span>}
                  <div className="nfx-2fa-method__t">{m.title}</div>
                  <div className="nfx-2fa-method__row">
                    <span className="nfx-2fa-method__ic">{m.icon}</span>
                    <span className="nfx-2fa-method__d">{m.desc}</span>
                  </div>
                  {m.cta && <button className="nfx-2fa-method__cta" disabled={busy} onClick={e => { e.stopPropagation(); setSel(m.key); beginTotp(); }}>{m.cta}</button>}
                </div>
              ))}
            </div>
            <div className="nfx-2fa-foot">
              <button className="nfx-btn-sec" onClick={() => go('/account/sicurezza')}>Annullare</button>
              <button className={`nfx-btn-pri${sel && !busy ? '' : ' is-disabled'}`} disabled={!sel || busy} onClick={beginTotp}>{busy ? 'Attendi…' : 'Aggiungi questo metodo'}</button>
            </div>
          </>
        )}

        {/* STEP 2 — configurazione OTP */}
        {step === 2 && (
          <>
            <h2 className="nfx-2fa-title">Configurazione di un'applicazione mobile OTP</h2>
            <p className="nfx-2fa-sub">Associa l'applicazione mobile OTP al tuo account utente seguendo le istruzioni sottostanti.</p>
            <ol className="nfx-2fa-steps-list">
              <li>Apri l'applicazione e aggiungi il tuo account utente facendo clic su «+».</li>
              <li>{showKey ? 'Inserisci manualmente la chiave seguente nella tua applicazione.' : 'Scansiona questo codice QR dal tuo telefono cellulare e verifica di essere correttamente connesso a Internet.'}</li>
            </ol>
            <div className="nfx-2fa-qrwrap">
              {showKey
                ? <div className="nfx-2fa-secret">{(secret || '').replace(/(.{4})/g, '$1 ').trim()}</div>
                : <div className="nfx-2fa-qr">{qr ? <img src={qr} alt="QR code per la 2FA" style={{ width: 180, height: 180, display: 'block' }} /> : <div className="nfx-2fa-secret">{secret}</div>}</div>}
              <button className="nfx-secr-link" onClick={() => setShowKey(k => !k)}>{showKey ? 'Preferisco scansionare il codice QR' : 'Non riesco a scansionare il codice QR'}</button>
            </div>
            <div className="nfx-2fa-foot">
              <button className="nfx-btn-sec" onClick={() => setStep(1)}>Annullare</button>
              <button className="nfx-btn-pri" onClick={() => setStep(3)}>Convalidare</button>
            </div>
          </>
        )}
        {/* STEP 3 — verifica codice */}
        {step === 3 && (
          <>
            <h2 className="nfx-2fa-title">Verifica</h2>
            <p className="nfx-2fa-sub">Inserisci il codice a 6 cifre generato dalla tua applicazione per completare la configurazione.</p>
            <div className="nfx-fld" style={{ maxWidth: 240 }}>
              <label className="nfx-fld__label">Codice di verifica *</label>
              <input className="nfx-fld__input nfx-2fa-otp" inputMode="numeric" maxLength={6} value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus placeholder="000000" />
            </div>
            <div className="nfx-2fa-foot">
              <button className="nfx-btn-sec" onClick={() => setStep(2)}>Annullare</button>
              <button className={`nfx-btn-pri${code.length === 6 && !busy ? '' : ' is-disabled'}`} disabled={code.length !== 6 || busy} onClick={confirmTotp}>{busy ? 'Verifica…' : 'Convalidare'}</button>
            </div>
          </>
        )}

        {/* STEP 4 — codici d'emergenza */}
        {step === 4 && (
          <>
            <div className="nfx-2fa-done"><span className="nfx-2fa-done__ic"><ShieldCheck size={30} /></span>
              <div>
                <h2 className="nfx-2fa-title" style={{ margin: 0 }}>Hai accresciuto la sicurezza del tuo account</h2>
                <p className="nfx-2fa-sub" style={{ margin: '6px 0 0' }}>Scarica i codici d'emergenza: potrai usarli per accedere se non hai a disposizione il tuo metodo di convalida.</p>
              </div>
            </div>
            <div className="nfx-2fa-codes">
              {codes.map((c, i) => <div key={i} className="nfx-2fa-code">{c}</div>)}
            </div>
            <div className="nfx-2fa-codes-actions">
              <button className="nfx-btn-sec" onClick={() => window.print()}><Printer size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />Stampare</button>
              <button className="nfx-btn-sec" onClick={downloadCodes}><Download size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />Scaricare</button>
            </div>
            <p className="nfx-2fa-warn"><AlertTriangle size={15} /> Conserva questi codici in un luogo sicuro e accessibile.</p>
            <div className="nfx-2fa-foot nfx-2fa-foot--end">
              <button className="nfx-btn-pri" onClick={finish}>Fine</button>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

/* ═══════════════════ 6. SUPPORTO ═══════════════════ */
export const SupportoAccountPage = () => {
  const [auth, setAuth] = useState({ mail: false, drive: false, chat: false });
  const cards = [
    { k: 'mail', t: "Autorizza temporaneamente l'Assistenza ad accedere al Servizio mail" },
    { k: 'drive', t: "Autorizza temporaneamente l'Assistenza ad accedere ai servizi cloud" },
    { k: 'chat', t: "Autorizza temporaneamente l'assistenza tecnica ad accedere ai messaggi" },
  ];
  return (
    <div className="nfx-cb">
      <AccCrumb current="Supporto" />
      <h1 className="nfx-cb-title">Supporto</h1>
      <div className="nfx-card nfx-card--pad">
        <div className="nfx-sec__title">Accesso all'Assistenza</div>
        <div className="nfx-sec__desc" style={{ marginTop: 8 }}>A volte, è necessario accedere al tuo account per risalire all'origine di un problema. L'autorizzazione è <b>valida 7 giorni</b> e può essere ritirata in qualsiasi momento.</div>
        <div className="nfx-auth-grid">
          {cards.map(c => (
            <div className="nfx-auth-card" key={c.k}>
              <div className="nfx-auth-card__top">
                <div className="nfx-auth-card__title">{c.t}</div>
                <Toggle on={auth[c.k]} onClick={() => setAuth(a => ({ ...a, [c.k]: !a[c.k] }))} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="nfx-card nfx-card--info nfx-card--pad" style={{ position: 'relative' }}>
        <div className="nfx-sec__title">Codice Assistenza</div>
        <div className="nfx-sec__desc" style={{ marginTop: 8 }}>Qualora tu contatti il nostro servizio di assistenza per telefono, ti preghiamo di fornire questo codice. Il nostro servizio di assistenza è disponibile dal lunedì al venerdì dalle 09:00 alle 18:00.</div>
        <SupportCode variant="page" />
      </div>
    </div>
  );
};

/* ═══════════════════ 7. SVILUPPATORE — TOKEN ═══════════════════ */

const tokFmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}, ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/* Toast di conferma (auto-dismiss) */
const TokToast = ({ text, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="nfx-tok-toast">
      <CheckCircle2 size={16} className="ok" /> <span>{text}</span>
      <button className="x" onClick={onClose} aria-label="Chiudi"><X size={16} /></button>
    </div>
  );
};

/* Modale: scope del token */
const ScopesModal = ({ token, scopeMap, onClose }) => {
  const { t: tr } = useLingua();
  return (
    <V2Modal title={tr('Scopes del tuo token')} onClose={onClose}
      footer={<button className="nfx-btn-sec" onClick={onClose}>{tr('Chiudi')}</button>}>
      <p className="nfx-modal__p">{tr("Ecco l'elenco degli scope autorizzati dal token")} <b>{token.nome || token.applicazione || tr('Applicazione predefinita')}</b></p>
      <div className="nfx-tok-scopes">
        {(token.scopes || []).map(s => (
          <div className="nfx-tok-scoperow" key={s}><b>{s}</b><span>{scopeMap[s] || ''}</span></div>
        ))}
      </div>
    </V2Modal>
  );
};

/* Modale: modifica nome/descrizione */
const EditTokenModal = ({ token, onClose, onSaved }) => {
  const { t: tr } = useLingua();
  const [nome, setNome] = useState(token.nome || '');
  const [desc, setDesc] = useState(token.descrizione || '');
  const [busy, setBusy] = useState(false);
  const save = () => {
    setBusy(true);
    api.patch(`/api/sviluppatore/token/${token.id}`, { nome, descrizione: desc })
      .then(() => onSaved()).catch(() => setBusy(false));
  };
  return (
    <V2Modal title={tr('Modificare le informazioni')} onClose={onClose}
      footer={<><button className="nfx-btn-sec" onClick={onClose}>{tr('Chiudi')}</button>
        <button className="nfx-btn-pri" onClick={save} disabled={busy}>{tr('Registrare')}</button></>}>
      <div className="nfx-tok-form">
        <label className="nfx-tok-field"><span>{tr('Nome')}</span><input value={nome} onChange={e => setNome(e.target.value)} /></label>
        <label className="nfx-tok-field nfx-tok-field--area"><span>{tr('Descrizione')}</span><textarea rows={3} value={desc} onChange={e => setDesc(e.target.value)} /></label>
      </div>
    </V2Modal>
  );
};

/* Modale: conferma revoca */
const DeleteTokenModal = ({ token, onClose, onDeleted }) => {
  const { t: tr } = useLingua();
  const [busy, setBusy] = useState(false);
  const del = () => {
    setBusy(true);
    api.delete(`/api/sviluppatore/token/${token.id}`).then(() => onDeleted()).catch(() => setBusy(false));
  };
  return (
    <V2Modal title={tr('Conferma')} onClose={onClose}
      footer={<><button className="nfx-btn-sec" onClick={onClose}>{tr('Annullare')}</button>
        <button className="nfx-acc-btn-danger" onClick={del} disabled={busy}>{tr('Sì, revoca')}</button></>}>
      <p className="nfx-modal__p">{tr('Revocare veramente il token')} <b>{token.nome || tr('senza nome')}</b>{tr('? L\'azione è irreversibile.')}</p>
    </V2Modal>
  );
};

export const TokenPage = () => {
  const { t: tr } = useLingua();
  const [tokens, setTokens] = useState(null);
  const [scopes] = useFetch('/api/sviluppatore/scopes', 'scopes', []);
  const [q, setQ] = useState('');
  const [view, setView] = useState('list');
  const [menu, setMenu] = useState(null);
  const [modal, setModal] = useState(null);   // { type, token }
  const [toast, setToast] = useState('');
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (loc.state?.toast) { setToast(loc.state.toast); window.history.replaceState({}, ''); }
  }, [loc.state]);

  const scopeMap = React.useMemo(() => Object.fromEntries((scopes || []).map(s => [s.v, s.l])), [scopes]);
  const load = React.useCallback(() => {
    api.get('/api/sviluppatore/token').then(r => setTokens(r.data.token || [])).catch(() => setTokens([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => setMenu(null);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const list = (tokens || []).filter(t => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (t.nome || '').toLowerCase().includes(s)
      || (t.descrizione || '').toLowerCase().includes(s)
      || (t.applicazione || '').toLowerCase().includes(s)
      || (t.scopes || []).some(x => x.toLowerCase().includes(s));
  });

  const scopesLabel = (t) => `${(t.scopes || []).length} ${tr('scope')}`;

  return (
    <div className="nfx-cb">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <AccCrumb current="Token API" />
        <button className="nfx-btn-blue" onClick={() => nav('/account/sviluppatore/token/nuovo')}><Plus size={16} /> {tr('Crea un token')}</button>
      </div>
      <h1 className="nfx-cb-title">{tr('Gestione dei token API')}</h1>
      <p className="nfx-cb-intro">{tr("L'API NeoFlux ti consente di gestire i prodotti tramite le tue interfacce senza passare dal Manager.")} <a href="https://developer.neoflux.it" target="_blank" rel="noopener">{tr('Consulta la documentazione.')}</a><br />{tr("Un token consente di consultare l'API NeoFlux con il proprio account utente. Il codice ottenuto è")} <b>{tr('riservato')}</b> {tr('e non deve essere divulgato per nessuna ragione.')}</p>

      <div className="nfx-card">
        <div className="nfx-tb"><span className="nfx-tb__count"><b>{list.length}</b> {tr('token')}</span>
          <div className="nfx-search-field" style={{ marginLeft: 12 }}><Search size={16} /><input value={q} onChange={e => setQ(e.target.value)} placeholder={tr("Cerca un'applicazione o uno scope")} /></div>
          <span className="nfx-tb__spacer" />
          <div className="nfx-tb__actions">
            <button className={`nfx-iconbtn${view === 'list' ? ' is-active' : ''}`} onClick={() => setView('list')}><List size={18} /></button>
            <button className={`nfx-iconbtn${view === 'cards' ? ' is-active' : ''}`} onClick={() => setView('cards')}><LayoutGrid size={18} /></button>
          </div>
        </div>

        {tokens === null ? (
          <div className="nfx-token-empty"><div className="nfx-token-empty__text">{tr('Caricamento…')}</div></div>
        ) : list.length === 0 ? (
          <div className="nfx-token-empty"><div className="nfx-token-empty__circle"><Search size={40} /></div><div className="nfx-token-empty__text">{tokens.length ? tr('Nessun risultato') : tr('Nessun token')}</div></div>
        ) : view === 'list' ? (
          <div className="nfx-tok-tablewrap">
            <table className="nfx-tok-table">
              <thead><tr><th>{tr('Nome')}</th><th>{tr('Creazione')}</th><th>{tr('Scadenza')}</th><th>{tr('Ultimo utilizzo')}</th><th>{tr('Ambiti')}</th><th>{tr('Descrizione')}</th><th /></tr></thead>
              <tbody>
                {list.map(t => (
                  <tr key={t.id}>
                    <td className="name">{t.nome || <span className="nfx-tok-muted">{tr('senza nome')}</span>}</td>
                    <td>{tokFmtDate(t.created_at) || '—'}</td>
                    <td>{t.scadenza ? tokFmtDate(t.scadenza) : tr('Illimitato')}</td>
                    <td>{t.ultimo_utilizzo ? tokFmtDate(t.ultimo_utilizzo) : tr('Non ancora in uso')}</td>
                    <td><button className="nfx-tok-link" onClick={() => setModal({ type: 'scopes', token: t })}>{scopesLabel(t)}</button></td>
                    <td className="desc">{t.descrizione || '—'}</td>
                    <td className="actions">
                      <div className="nfx-rowmenu">
                        <button className="nfx-rowmenu__btn" onClick={(e) => { e.stopPropagation(); setMenu(menu === t.id ? null : t.id); }} aria-label="Azioni"><MoreVertical size={16} /></button>
                        {menu === t.id && (
                          <div className="nfx-rowmenu__pop" onClick={e => e.stopPropagation()}>
                            <button className="nfx-rowmenu__item" onClick={() => { setMenu(null); setModal({ type: 'edit', token: t }); }}><Pencil size={14} /> {tr('Modificare le informazioni')}</button>
                            <button className="nfx-rowmenu__item nfx-rowmenu__item--danger" onClick={() => { setMenu(null); setModal({ type: 'del', token: t }); }}><Trash2 size={14} /> {tr('Elimina il token')}</button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="nfx-tok-cards">
            {list.map(t => (
              <div className="nfx-tok-card" key={t.id}>
                <div>
                  <div className="nfx-tok-card__name">{t.nome || tr('senza nome')}</div>
                  <div className="nfx-tok-card__row">{tr('Creazione')}: {tokFmtDate(t.created_at) || '—'}</div>
                  <div className="nfx-tok-card__row">{tr('Scadenza')}: {t.scadenza ? tokFmtDate(t.scadenza) : tr('Illimitato')}</div>
                  <div className="nfx-tok-card__row">{tr('Ultimo utilizzo')}: {t.ultimo_utilizzo ? tokFmtDate(t.ultimo_utilizzo) : tr('Non ancora in uso')}</div>
                  {t.descrizione && <div className="nfx-tok-card__row">{tr('Descrizione')}: {t.descrizione}</div>}
                </div>
                <div className="nfx-tok-card__side">
                  <div className="nfx-rowmenu">
                    <button className="nfx-rowmenu__btn" onClick={(e) => { e.stopPropagation(); setMenu(menu === t.id ? null : t.id); }} aria-label="Azioni"><MoreVertical size={16} /></button>
                    {menu === t.id && (
                      <div className="nfx-rowmenu__pop" onClick={e => e.stopPropagation()}>
                        <button className="nfx-rowmenu__item" onClick={() => { setMenu(null); setModal({ type: 'edit', token: t }); }}><Pencil size={14} /> {tr('Modificare le informazioni')}</button>
                        <button className="nfx-rowmenu__item nfx-rowmenu__item--danger" onClick={() => { setMenu(null); setModal({ type: 'del', token: t }); }}><Trash2 size={14} /> {tr('Elimina il token')}</button>
                      </div>
                    )}
                  </div>
                  <button className="nfx-tok-link" onClick={() => setModal({ type: 'scopes', token: t })}>{scopesLabel(t)}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal?.type === 'scopes' && <ScopesModal token={modal.token} scopeMap={scopeMap} onClose={() => setModal(null)} />}
      {modal?.type === 'edit' && <EditTokenModal token={modal.token} onClose={() => setModal(null)} onSaved={() => { setModal(null); setToast(tr('Informazioni aggiornate')); load(); }} />}
      {modal?.type === 'del' && <DeleteTokenModal token={modal.token} onClose={() => setModal(null)} onDeleted={() => { setModal(null); setToast(tr('Token revocato')); load(); }} />}
      {toast && <TokToast text={toast} onClose={() => setToast('')} />}
    </div>
  );
};

/* ── Pagina: Creazione di un token ── */
const DURATE = [
  { v: '1h', l: '1 ora' }, { v: '1d', l: '1 giorno' }, { v: '1mo', l: '1 mese' },
  { v: '6mo', l: '6 mesi' }, { v: '1y', l: '1 anno' }, { v: 'unlimited', l: 'Illimitato' },
];

export const CreateTokenPage = () => {
  const { t: tr } = useLingua();
  const nav = useNavigate();
  const [scopeCat] = useFetch('/api/sviluppatore/scopes', 'scopes', []);
  const [nome, setNome] = useState('');
  const [desc, setDesc] = useState('');
  const [sel, setSel] = useState([]);          // scope selezionati
  const [durata, setDurata] = useState('unlimited');
  const [scopeQ, setScopeQ] = useState('');
  const [openScope, setOpenScope] = useState(false);
  const [openDur, setOpenDur] = useState(false);
  const [step, setStep] = useState('form');    // form | password | token
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [tokenValue, setTokenValue] = useState('');
  const [copied, setCopied] = useState(false);

  const filtered = (scopeCat || []).filter(s =>
    !sel.includes(s.v) && (`${s.v} ${s.l}`.toLowerCase().includes(scopeQ.toLowerCase())));

  const create = () => {
    setBusy(true); setErr('');
    api.post('/api/sviluppatore/token', { nome, descrizione: desc, scopes: sel, durata, applicazione: 'Applicazione predefinita', password: pw })
      .then(r => { setTokenValue(r.data.token); setStep('token'); })
      .catch(e => setErr(e.response?.data?.messaggio || tr('Errore durante la creazione')))
      .finally(() => setBusy(false));
  };
  const copy = () => { navigator.clipboard?.writeText(tokenValue); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const finish = () => nav('/account/sviluppatore/token', { state: { toast: tr('Token creato con successo') } });

  return (
    <div className="nfx-cb">
      <AccCrumb current="Creazione di un token" parents={[{ label: 'Token API', path: '/account/sviluppatore/token' }]} />
      <div className="nfx-card" style={{ padding: 25 }}>
        <h1 className="nfx-cb-title" style={{ fontSize: 20, margin: '0 0 8px' }}>{tr('Creazione di un token')}</h1>
        <p className="nfx-cb-intro" style={{ marginBottom: 24 }}>{tr('Per creare il tuo token compila i campi sottostanti:')}</p>

        <div className="nfx-tok-form">
          <label className="nfx-tok-field"><span>{tr('Nome del token (facoltativo)')}</span>
            <input value={nome} onChange={e => setNome(e.target.value)} /></label>

          <div className="nfx-tok-field nfx-tok-field--static"><span>{tr('Applicazione *')}</span>
            <div className="nfx-tok-fakeselect">{tr('Applicazione predefinita')}</div></div>

          <div className="nfx-tok-field nfx-tok-chipsfield" onClick={() => setOpenScope(true)}>
            <span>{tr('Ambiti *')}</span>
            <div className="nfx-tok-chips">
              {sel.map(v => (
                <span className="nfx-tok-chip" key={v}>{v}
                  <button onClick={(e) => { e.stopPropagation(); setSel(sel.filter(x => x !== v)); }} aria-label={tr('Rimuovi')}><X size={14} /></button></span>
              ))}
              <input value={scopeQ} onChange={e => { setScopeQ(e.target.value); setOpenScope(true); }}
                onFocus={() => setOpenScope(true)}
                onBlur={() => setTimeout(() => setOpenScope(false), 150)}
                placeholder={sel.length ? '' : tr('Cercare uno scope')} />
            </div>
            {openScope && (
              <div className="nfx-tok-panel" onMouseDown={e => e.preventDefault()}>
                {sel.length < (scopeCat || []).length && (
                  <div className="nfx-tok-panel__opt" onClick={() => { setSel((scopeCat || []).map(s => s.v)); setScopeQ(''); }}>{tr('Tutti')}</div>
                )}
                {filtered.map(s => (
                  <div className="nfx-tok-panel__opt" key={s.v} onClick={() => { setSel([...sel, s.v]); setScopeQ(''); }}>{s.v} — {s.l}</div>
                ))}
                {filtered.length === 0 && sel.length >= (scopeCat || []).length && <div className="nfx-tok-panel__opt is-empty">{tr('Tutti gli scope selezionati')}</div>}
              </div>
            )}
          </div>

          <div className="nfx-tok-field nfx-tok-selectfield" onClick={() => setOpenDur(!openDur)}>
            <span>{tr('Durata di validità *')}</span>
            <div className="nfx-tok-selectval">{tr(DURATE.find(d => d.v === durata)?.l)}<ChevronDown size={16} /></div>
            {openDur && (
              <div className="nfx-tok-panel" onMouseDown={e => e.preventDefault()}>
                {DURATE.map(d => (
                  <div className={`nfx-tok-panel__opt${d.v === durata ? ' is-selected' : ''}`} key={d.v}
                    onClick={(e) => { e.stopPropagation(); setDurata(d.v); setOpenDur(false); }}>{tr(d.l)}</div>
                ))}
              </div>
            )}
          </div>

          <label className="nfx-tok-field nfx-tok-field--area"><span>{tr('Descrizione (facoltativa)')}</span>
            <textarea rows={3} value={desc} onChange={e => setDesc(e.target.value)} /></label>
        </div>

        <div className="nfx-tok-actions">
          <button className="nfx-btn-sec" onClick={() => nav('/account/sviluppatore/token')}>{tr('Chiudi')}</button>
          <button className="nfx-btn-pri" disabled={sel.length === 0} onClick={() => { setErr(''); setStep('password'); }}>{tr('Crea il token')}</button>
        </div>
      </div>

      {step === 'password' && (
        <V2Modal title={tr('Password di accesso')} onClose={() => setStep('form')}
          footer={<><button className="nfx-btn-sec" onClick={() => setStep('form')}>{tr('Chiudi')}</button>
            <button className="nfx-btn-pri" disabled={!pw || busy} onClick={create}>{tr('Convalidare')}</button></>}>
          <p className="nfx-modal__p">{tr('Inserisci la password del tuo account per creare il token.')}</p>
          <div className="nfx-tok-pwfield">
            <input type={showPw ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)}
              placeholder={tr('Password di accesso *')} autoFocus onKeyDown={e => { if (e.key === 'Enter' && pw && !busy) create(); }} />
            <button onClick={() => setShowPw(!showPw)} aria-label={tr('Mostra password')}>{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
          </div>
          {err && <div className="nfx-tok-err">{err}</div>}
        </V2Modal>
      )}

      {step === 'token' && (
        <V2Modal title={tr('Token')} onClose={finish}
          footer={<button className="nfx-btn-sec" onClick={finish}>{tr('Chiudi')}</button>}>
          <p className="nfx-modal__p">{tr('Ricordati di copiare il tuo token: in un secondo momento non sarà più accessibile.')}</p>
          <div className="nfx-tok-box">
            <span>{tokenValue}</span>
            <button onClick={copy} aria-label={tr('Copia')}>{copied ? <Check size={16} /> : <Copy size={16} />}</button>
          </div>
        </V2Modal>
      )}
    </div>
  );
};

